'use server';

import { signIn, auth, signOut } from '@/auth';
import { AuthError } from 'next-auth';
import { Prisma, PrismaClient } from '@prisma/client';
import { Pool } from 'pg';
import { PrismaPg } from '@prisma/adapter-pg';
import { revalidatePath } from 'next/cache';
import { redirect } from 'next/navigation';
import {
  ClientFormSchema,
  PageFormSchema,
  ContactFormSchema,
  LeadFormSchema,
  OpportunityFormSchema,
  ArticleFormSchema,
  DocumentFormSchema,
  ServiceLineFormSchema,
  ActivityFormSchema,
  CommentFormSchema,
  InvoiceFormSchema,
  ProjectFormSchema,
  PhaseFormSchema,
  ProjectImportSchema,
  ProjectTemplateFormSchema,
  TemplatePhaseFormSchema,
  TemplateActivityFormSchema,
  parseDiscountsJson,
  parseFeesJson,
} from './definitions';
import { fetchActivitiesByPhaseId } from './data';
import { sendOpportunityCreatedWhatsAppNotification } from './whatsapp';

const pool = new Pool({
  connectionString: process.env.DATABASE_URL,
});

const adapter = new PrismaPg(pool);

const prisma = new PrismaClient({ adapter });

const CreateClient = ClientFormSchema.omit({ id: true });
const UpdateClient = ClientFormSchema;
const CreateProject = ProjectFormSchema.omit({ id: true });
const UpdateProject = ProjectFormSchema;
const CreatePhase = PhaseFormSchema.omit({ id: true });
const UpdatePhase = PhaseFormSchema;
const CreatePage = PageFormSchema.omit({ id: true });
const UpdatePage = PageFormSchema;
const CreateContact = ContactFormSchema.omit({ id: true });
const CreateLead = LeadFormSchema.omit({ id: true });
const UpdateLead = LeadFormSchema;
const CreateOpportunity = OpportunityFormSchema.omit({ id: true });
const UpdateOpportunity = OpportunityFormSchema;
const CreateArticle = ArticleFormSchema.omit({ id: true });
const UpdateArticle = ArticleFormSchema;
const CreateDocument = DocumentFormSchema.omit({ id: true });
const CreateServiceLine = ServiceLineFormSchema.omit({ id: true });
const UpdateDocument = DocumentFormSchema;
const UpdateServiceLine = ServiceLineFormSchema;
const CreateActivity = ActivityFormSchema.omit({ id: true });
const UpdateActivity = ActivityFormSchema;
const CreateComment = CommentFormSchema.omit({ id: true });
const CreateInvoice = InvoiceFormSchema.omit({ id: true });
const UpdateInvoice = InvoiceFormSchema;
const CreateProjectTemplate = ProjectTemplateFormSchema.omit({ id: true });
const UpdateProjectTemplate = ProjectTemplateFormSchema;
const CreateTemplatePhase = TemplatePhaseFormSchema.omit({ id: true });
const UpdateTemplatePhase = TemplatePhaseFormSchema;
const CreateTemplateActivity = TemplateActivityFormSchema.omit({ id: true });
const UpdateTemplateActivity = TemplateActivityFormSchema;

type InvoiceItem = {
  activityId?: string;
  description: string;
  detailsHtml?: string | null;
  date?: Date;
  hours: number;
  rate: number;
  discount?: number;
  amount: number;
};

type InvoiceMeta = {
  useDatiCassaPrevidenziale?: boolean;
  invoiceDiscount?: number;
};

type InvoiceItemsPayload =
  | InvoiceItem[]
  | {
      items: InvoiceItem[];
      meta?: InvoiceMeta;
    };

const webhookUrl = process.env.N8N_WEBHOOK_URL;

const defaultHourlyRateEnv = process.env.DEFAULT_HOURLY_RATE;
const defaultHourlyRate =
  defaultHourlyRateEnv && !Number.isNaN(Number(defaultHourlyRateEnv))
    ? Number(defaultHourlyRateEnv)
    : 50;

async function resolveHourlyRateForClient(
  clientId: string,
) {
  const client = await prisma.client.findUnique({
    where: { id: clientId },
    select: {
      hourlyRate: true,
    },
  });

  if (client?.hourlyRate != null && client.hourlyRate > 0) {
    return client.hourlyRate;
  }

  return defaultHourlyRate;
}

async function sendWebhook(event: {
  entity: string;
  action: string;
  data: unknown;
}) {
  if (!webhookUrl) {
    return;
  }

  try {
    await fetch(webhookUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(event),
    });
  } catch (error) {
    console.error('Errore webhook', error);
  }
}

 
export async function authenticate(
  prevState: string | undefined,
  formData: FormData,
) {
  try {
    await signIn('credentials', formData);
  } catch (error) {
    if (error instanceof AuthError) {
      switch (error.type) {
        case 'CredentialsSignin':
          return 'Invalid credentials.';
        default:
          return 'Something went wrong.';
      }
    }
    throw error;
  }
}

export async function logout() {
  await signOut({ redirectTo: '/login' });
}

export type State = {
  errors?: {
    [key: string]: string[] | undefined;
  };
  message?: string | null;
};

export type CommentFormState = {
  message: string | null;
  errors?: {
    opportunityId?: string[];
    content?: string[];
  };
};

export type ContactFormState = {
  message: string | null;
  errors?: {
    clientId?: string[];
    firstName?: string[];
    lastName?: string[];
    role?: string[];
    email?: string[];
    phone?: string[];
  };
};

export async function completeActivityQuick(
  prevState: State,
  formData: FormData,
) {
  const id = formData.get('id');

  if (typeof id !== 'string' || !id) {
    return {
      ...prevState,
      message: 'Attività non valida.',
    };
  }

  const hoursRaw = formData.get('hours');
  const startTimeRaw = formData.get('startTime');
  const endTimeRaw = formData.get('endTime');

  const hours =
    typeof hoursRaw === 'string' && hoursRaw !== '' ? Number(hoursRaw) : 0;

  const startTime =
    typeof startTimeRaw === 'string' && startTimeRaw !== ''
      ? startTimeRaw
      : null;
  const endTime =
    typeof endTimeRaw === 'string' && endTimeRaw !== '' ? endTimeRaw : null;

  if (Number.isNaN(hours) || hours < 0) {
    return {
      errors: {
        hours: ['Le ore devono essere un numero non negativo'],
      },
      message: 'Campi mancanti. Aggiornamento attività non riuscito.',
    };
  }

  const hasHours = hours > 0;
  const hasTimes = !!startTime && !!endTime;

  if (!hasHours && !hasTimes) {
    return {
      errors: {
        hours: [
          'Per le attività completate indicare le ore, l’orario di inizio/fine o un importo',
        ],
      },
      message: 'Campi mancanti. Aggiornamento attività non riuscito.',
    };
  }

  const activity = await prisma.activity.findUnique({
    where: { id },
    select: {
      date: true,
      clientId: true,
    },
  });

  if (!activity) {
    return {
      ...prevState,
      message: 'Attività non trovata.',
    };
  }

  const hourlyRate = await resolveHourlyRateForClient(activity.clientId);
  const calculatedAmount = hours * hourlyRate;

  const dateString = activity.date.toISOString().split('T')[0];

  const startDateTime = startTime
    ? new Date(`${dateString}T${startTime}`)
    : null;
  const endDateTime = endTime ? new Date(`${dateString}T${endTime}`) : null;

  try {
    const updated = await prisma.activity.update({
      where: { id },
      data: {
        status: 'DONE',
        hours,
        startTime: startDateTime,
        endTime: endDateTime,
        amountSnapshot: calculatedAmount,
        hourlyRateSnapshot: hourlyRate,
      },
    });

    await sendWebhook({
      entity: 'activity',
      action: 'quickComplete',
      data: updated,
    });
  } catch (error) {
    console.error('Update activity error:', error);
    return {
      message: 'Errore database: aggiornamento attività non riuscito.',
    };
  }

  revalidatePath('/gestionale/dashboard');
  revalidatePath('/gestionale/dashboard/activities');
  return {
    message: null,
    errors: {},
  };
}

export async function startActivityNow(id: string, _formData: FormData) {
  const now = new Date();
  const dateOnly = new Date(
    now.getFullYear(),
    now.getMonth(),
    now.getDate(),
  );

  try {
    const activity = await prisma.activity.update({
      where: { id },
      data: {
        date: dateOnly,
        startTime: now,
      },
    });

    await sendWebhook({
      entity: 'activity',
      action: 'start',
      data: activity,
    });
  } catch (error) {
    console.error('Errore database: avvio attività non riuscito.', error);
    throw new Error('Errore database: avvio attività non riuscito.');
  }

  revalidatePath('/gestionale/dashboard');
  revalidatePath('/gestionale/dashboard/activities');
}

export async function stopActivityNow(id: string, _formData: FormData) {
  const now = new Date();

  const activity = await prisma.activity.findUnique({
    where: { id },
    select: {
      startTime: true,
      date: true,
      clientId: true,
    },
  });

  if (!activity) {
    throw new Error('Attività non trovata.');
  }

  const start = activity.startTime || now;
  const diffMs = now.getTime() - start.getTime();
  const hours = diffMs > 0 ? diffMs / (1000 * 60 * 60) : 0;

  const hourlyRate = await resolveHourlyRateForClient(activity.clientId);
  const amount = hours * hourlyRate;

  try {
    const updated = await prisma.activity.update({
      where: { id },
      data: {
        status: 'DONE',
        endTime: now,
        hours,
        hourlyRateSnapshot: hourlyRate,
        amountSnapshot: amount,
      },
    });

    await sendWebhook({
      entity: 'activity',
      action: 'stop',
      data: updated,
    });
  } catch (error) {
    console.error('Errore database: completamento rapido attività non riuscito.', error);
    throw new Error('Errore database: completamento rapido attività non riuscito.');
  }

  revalidatePath('/gestionale/dashboard');
  revalidatePath('/gestionale/dashboard/activities');
}

export async function resetActivity(id: string, _formData: FormData) {
  try {
    const activity = await prisma.activity.update({
      where: { id },
      data: {
        status: 'TODO',
        startTime: null,
        endTime: null,
        hours: 0,
        amountSnapshot: null,
      },
    });

    await sendWebhook({
      entity: 'activity',
      action: 'reset',
      data: activity,
    });
  } catch (error) {
    console.error('Errore database: reset attività non riuscito.', error);
    throw new Error('Errore database: reset attività non riuscito.');
  }

  revalidatePath('/gestionale/dashboard');
  revalidatePath('/gestionale/dashboard/activities');
}

export type PageFormState = {
  message: string | null;
  errors?: {
    [key: string]: string[] | undefined;
  };
};

export async function createClient(prevState: State, formData: FormData) {
  const validatedFields = CreateClient.safeParse({
    name: formData.get('name'),
    vatNumber: formData.get('vatNumber'),
    fiscalCode: formData.get('fiscalCode'),
    pec: formData.get('pec'),
    sdiCode: formData.get('sdiCode'),
    address: formData.get('address'),
    city: formData.get('city'),
    province: formData.get('province'),
    zipCode: formData.get('zipCode'),
    country: formData.get('country') || 'Italia',
    email: formData.get('email'),
    phone: formData.get('phone'),
    hourlyRate: formData.get('hourlyRate'),
  });

  if (!validatedFields.success) {
    return {
      errors: validatedFields.error.flatten().fieldErrors,
      message: 'Missing Fields. Failed to Create Client.',
    };
  }

  const data = validatedFields.data;

  try {
    const client = await prisma.client.create({
      data: {
        ...data,
      },
    });

    await sendWebhook({
      entity: 'client',
      action: 'create',
      data: client,
    });
  } catch (error) {
    return {
      message: 'Database Error: Failed to Create Client.',
    };
  }

  revalidatePath('/gestionale/dashboard/clients');
  redirect('/dashboard/clients');
}

export async function updateClient(
  id: string,
  prevState: State,
  formData: FormData,
) {
  const validatedFields = UpdateClient.safeParse({
    id: id,
    name: formData.get('name'),
    vatNumber: formData.get('vatNumber'),
    fiscalCode: formData.get('fiscalCode'),
    pec: formData.get('pec'),
    sdiCode: formData.get('sdiCode'),
    address: formData.get('address'),
    city: formData.get('city'),
    province: formData.get('province'),
    zipCode: formData.get('zipCode'),
    country: formData.get('country') ?? undefined,
    email: formData.get('email'),
    phone: formData.get('phone'),
    hourlyRate: formData.get('hourlyRate'),
  });

  if (!validatedFields.success) {
    return {
      errors: validatedFields.error.flatten().fieldErrors,
      message: 'Missing Fields. Failed to Update Client.',
    };
  }

  const data = validatedFields.data;

  try {
    const client = await prisma.client.update({
      where: { id },
      data: {
        ...data,
      },
    });

    await sendWebhook({
      entity: 'client',
      action: 'update',
      data: client,
    });
  } catch (error) {
    return { message: 'Database Error: Failed to Update Client.' };
  }

  revalidatePath('/gestionale/dashboard/clients');
  redirect('/dashboard/clients');
}

export async function deleteClient(id: string, _formData: FormData) {
  try {
    const client = await prisma.client.delete({
      where: { id },
    });
    revalidatePath('/gestionale/dashboard/clients');
  } catch (error) {
    console.error('Database Error: Failed to Delete Client.', error);
    throw new Error('Database Error: Failed to Delete Client.');
  }

  await sendWebhook({
    entity: 'client',
    action: 'delete',
    data: {
      id,
    },
  });
}

export async function createContact(
  prevState: ContactFormState,
  formData: FormData,
): Promise<ContactFormState> {
  const validatedFields = CreateContact.safeParse({
    clientId: formData.get('clientId'),
    firstName: formData.get('firstName'),
    lastName: formData.get('lastName'),
    role: formData.get('role'),
    email: formData.get('email'),
    phone: formData.get('phone'),
  });

  if (!validatedFields.success) {
    return {
      message: 'Campi mancanti. Creazione contatto non riuscita.',
      errors: validatedFields.error.flatten().fieldErrors,
    };
  }

  const data = validatedFields.data;

  try {
    const contact = await prisma.contact.create({
      data: {
        firstName: data.firstName,
        lastName: data.lastName,
        email: data.email || null,
        phone: data.phone || null,
        clientId: data.clientId,
      },
    });

    await sendWebhook({
      entity: 'contact',
      action: 'create',
      data: contact,
    });
  } catch (error) {
    return {
      message: 'Errore database: creazione contatto non riuscita.',
    };
  }

  revalidatePath(`/gestionale/dashboard/clients/${data.clientId}/edit`);
  return { message: null };
}

export async function deleteContact(id: string, formData: FormData) {
  const clientId = formData.get('clientId')?.toString() || '';

  try {
    const contact = await prisma.contact.delete({
      where: { id },
    });
  } catch (error) {
    console.error('Errore database: eliminazione contatto non riuscita.', error);
    throw new Error('Errore database: eliminazione contatto non riuscita.');
  }

  await sendWebhook({
    entity: 'contact',
    action: 'delete',
    data: {
      id,
      clientId: clientId || undefined,
    },
  });

  if (clientId) {
    revalidatePath(`/gestionale/dashboard/clients/${clientId}/edit`);
  } else {
    revalidatePath('/gestionale/dashboard/clients');
  }
}

export async function createPage(
  prevState: PageFormState,
  formData: FormData,
): Promise<PageFormState> {
  const validatedFields = CreatePage.safeParse({
    title: formData.get('title'),
    slug: formData.get('slug'),
    parentId: formData.get('parentId'),
    description: formData.get('description'),
    status: formData.get('status') || 'DRAFT',
    seoTitle: formData.get('seoTitle'),
    seoDescription: formData.get('seoDescription'),
    seoKeywords: formData.get('seoKeywords'),
    canonicalUrl: formData.get('canonicalUrl'),
    ogImageUrl: formData.get('ogImageUrl'),
    inHome: formData.get('inHome') === 'on',
    inMenu: formData.get('inMenu') === 'on',
    priority: formData.get('priority'),
    titoloHome: formData.get('titoloHome'),
    sottotitoloHome: formData.get('sottotitoloHome'),
    ctaHomeText: formData.get('ctaHomeText'),
    ctaHomeLink: formData.get('ctaHomeLink'),
    homeImage: formData.get('homeImage'),
    homeVideo: formData.get('homeVideo'),
  });

  if (!validatedFields.success) {
    return {
      message: 'Campi mancanti. Creazione pagina non riuscita.',
      errors: validatedFields.error.flatten().fieldErrors,
    };
  }

  const data = validatedFields.data;

  let blocks: any[] = [];
  const rawBlocks = formData.get('blocks');
  if (rawBlocks && typeof rawBlocks === 'string') {
    try {
      const parsed = JSON.parse(rawBlocks);
      if (Array.isArray(parsed)) {
        blocks = parsed;
      }
    } catch (e) {
      blocks = [];
    }
  }

  const metadata = {
    titoloHome: data.titoloHome,
    sottotitoloHome: data.sottotitoloHome,
    ctaHome: {
      testo: data.ctaHomeText,
      link: data.ctaHomeLink,
    },
    image: data.homeImage,
    video: data.homeVideo,
  };

  try {
    const page = await prisma.page.create({
      data: {
        title: data.title,
        slug: data.slug,
        parentId: data.parentId || null,
        description: data.description || null,
        status: data.status as any,
        seoTitle: data.seoTitle || null,
        seoDescription: data.seoDescription || null,
        seoKeywords: data.seoKeywords || null,
        canonicalUrl: data.canonicalUrl || null,
        ogImageUrl: data.ogImageUrl || null,
        inHome: data.inHome,
        inMenu: data.inMenu,
        priority: data.priority,
        metadata: metadata,
        blocks: {
            create: blocks.map((block, index) => ({
              type: block.type,
              order: index,
              content: block, // Store full block content (including title, subtitle, items, etc.)
            })),
          },
      },
    });

    await sendWebhook({
      entity: 'page',
      action: 'create',
      data: page,
    });
  } catch (error) {
    return {
      message: 'Errore database: creazione pagina non riuscita.',
    };
  }

  revalidatePath('/gestionale/dashboard/pages');
  redirect('/dashboard/pages');
}

export async function updatePage(
  id: string,
  prevState: PageFormState,
  formData: FormData,
): Promise<PageFormState> {
  const validatedFields = UpdatePage.safeParse({
    id,
    title: formData.get('title'),
    slug: formData.get('slug'),
    parentId: formData.get('parentId'),
    description: formData.get('description'),
    status: formData.get('status'),
    seoTitle: formData.get('seoTitle'),
    seoDescription: formData.get('seoDescription'),
    seoKeywords: formData.get('seoKeywords'),
    canonicalUrl: formData.get('canonicalUrl'),
    ogImageUrl: formData.get('ogImageUrl'),
    inHome: formData.get('inHome') === 'on',
    inMenu: formData.get('inMenu') === 'on',
    priority: formData.get('priority'),
    titoloHome: formData.get('titoloHome'),
    sottotitoloHome: formData.get('sottotitoloHome'),
    ctaHomeText: formData.get('ctaHomeText'),
    ctaHomeLink: formData.get('ctaHomeLink'),
    homeImage: formData.get('homeImage'),
    homeVideo: formData.get('homeVideo'),
  });

  if (!validatedFields.success) {
    return {
      message: 'Campi mancanti. Aggiornamento pagina non riuscito.',
      errors: validatedFields.error.flatten().fieldErrors,
    };
  }

  const data = validatedFields.data;

  let blocks: any[] = [];
  const rawBlocks = formData.get('blocks');
  if (rawBlocks && typeof rawBlocks === 'string') {
    try {
      const parsed = JSON.parse(rawBlocks);
      if (Array.isArray(parsed)) {
        blocks = parsed;
      }
    } catch (e) {
      blocks = [];
    }
  }

  const metadata = {
    titoloHome: data.titoloHome,
    sottotitoloHome: data.sottotitoloHome,
    ctaHome: {
      testo: data.ctaHomeText,
      link: data.ctaHomeLink,
    },
    image: data.homeImage,
    video: data.homeVideo,
  };

  try {
    const [, page] = await prisma.$transaction([
      prisma.pageBlock.deleteMany({
        where: { pageId: id },
      }),
      prisma.page.update({
        where: { id },
        data: {
          title: data.title,
          slug: data.slug,
          parentId: data.parentId || null,
          description: data.description || null,
          status: data.status as any,
          seoTitle: data.seoTitle || null,
          seoDescription: data.seoDescription || null,
          seoKeywords: data.seoKeywords || null,
          canonicalUrl: data.canonicalUrl || null,
          ogImageUrl: data.ogImageUrl || null,
          inHome: data.inHome,
          inMenu: data.inMenu,
          priority: data.priority,
          metadata: metadata,
          blocks: {
            create: blocks.map((block, index) => ({
              type: block.type,
              order: index,
              content: block,
            })),
          },
        },
      }),
    ]);

    await sendWebhook({
      entity: 'page',
      action: 'update',
      data: page,
    });
  } catch (error) {
    return {
      message: 'Errore database: aggiornamento pagina non riuscito.',
    };
  }

  revalidatePath('/gestionale/dashboard/pages');
  redirect('/dashboard/pages');
}

export async function deletePage(id: string, _formData: FormData) {
  try {
    await prisma.pageBlock.deleteMany({
      where: { pageId: id },
    });

    await prisma.page.delete({
      where: { id },
    });

    revalidatePath('/gestionale/dashboard/pages');
  } catch (error) {
    console.error('Errore database: eliminazione pagina non riuscita.', error);
    throw new Error('Errore database: eliminazione pagina non riuscita.');
  }

  await sendWebhook({
    entity: 'page',
    action: 'delete',
    data: { id },
  });
}

export async function createProject(prevState: State, formData: FormData) {
  const validatedFields = CreateProject.safeParse({
    name: formData.get('name'),
    description: formData.get('description'),
    status: formData.get('status') || 'ACTIVE',
    clientId: formData.get('clientId'),
    startDate: formData.get('startDate'),
    endDate: formData.get('endDate'),
  });

  if (!validatedFields.success) {
    return {
      errors: validatedFields.error.flatten().fieldErrors,
      message: 'Campi mancanti. Creazione progetto non riuscita.',
    };
  }

  const data = validatedFields.data;
  const templateId = formData.get('templateId') as string | null;

  let project;
  try {
    // Crea il progetto (solo campi accettati da Prisma; paymentMethod non usato in create)
    project = await prisma.project.create({
      data: {
        name: data.name,
        description: data.description || null,
        status: data.status,
        clientId: data.clientId,
        startDate: data.startDate ? new Date(data.startDate) : null,
        endDate: data.endDate ? new Date(data.endDate) : null,
        color: data.color || null,
      },
    });

    // Se c'è un template, crea le fasi e le attività
    if (templateId) {
      const template = await prisma.projectTemplate.findUnique({
        where: { id: templateId },
        include: {
          phases: {
            orderBy: { order: 'asc' },
            include: {
              activities: {
                orderBy: { order: 'asc' },
              },
            },
          },
        },
      });

      if (template) {
        // Crea le fasi del progetto
        for (const templatePhase of template.phases) {
          const phase = await prisma.phase.create({
            data: {
              projectId: project.id,
              name: templatePhase.name,
              description: templatePhase.description,
              plannedDuration: templatePhase.plannedDuration,
              order: templatePhase.order,
              status: 'TODO',
            },
          });

          // Crea le attività del progetto (come "unplanned"); stato fatturazione default Esclusa
          for (const templateActivity of templatePhase.activities) {
            await prisma.activity.create({
              data: {
                description: templateActivity.description,
                publicTitle: templateActivity.publicTitle,
                hours: templateActivity.hours || 0,
                category: templateActivity.category,
                mode: templateActivity.mode,
                projectId: project.id,
                phaseId: phase.id,
                clientId: project.clientId,
                status: 'TODO',
                billingStatus: 'EXCLUDED',
                completed: false, // Attività da pianificare
                plannedDate: null, // Non pianificata
              },
            });
          }
        }

        // Calcola automaticamente le date delle fasi se c'è una data di inizio progetto
        if (project.startDate) {
          await calculatePhaseDates(project.id);
        }
      }
    }

    await sendWebhook({
      entity: 'project',
      action: 'create',
      data: project,
    });
  } catch (error) {
    console.error('Errore creazione progetto:', error);
    return {
      message: 'Errore database: creazione progetto non riuscita.',
    };
  }

  revalidatePath('/gestionale/dashboard/projects');
  redirect(`/dashboard/projects/${project.id}`);
}

export async function importProjectFromJson(clientId: string, jsonString: string) {
  let data: unknown;
  try {
    data = JSON.parse(jsonString);
  } catch {
    return { ok: false, error: 'JSON non valido.' };
  }

  const parsed = ProjectImportSchema.safeParse(data);
  if (!parsed.success) {
    const flat = parsed.error.flatten();
    const formErrors = flat.formErrors.filter(Boolean);
    const fieldErrors = Object.values(flat.fieldErrors).flat().filter(Boolean);
    const msg = [...formErrors, ...fieldErrors].join(' ') || 'Dati non validi. Verifica lo schema JSON.';
    return { ok: false, error: msg };
  }

  const payload = parsed.data;

  try {
    const projectData: any = {
      name: payload.name,
      description: payload.description ?? null,
      status: payload.status,
      clientId,
      startDate: payload.startDate ? new Date(payload.startDate) : null,
      endDate: payload.endDate ? new Date(payload.endDate) : null,
      pricingExtras:
        (payload as any).cosaOtterrai && Array.isArray((payload as any).cosaOtterrai)
          ? { whatYouGet: (payload as any).cosaOtterrai }
          : null,
    };
    const project = await prisma.project.create({ data: projectData });

    for (const phasePayload of payload.phases) {
      const phase = await prisma.phase.create({
        data: {
          projectId: project.id,
          name: phasePayload.name,
          description: phasePayload.description ?? null,
          plannedDuration: phasePayload.plannedDuration,
          order: phasePayload.order,
          amount: phasePayload.amount ?? null,
          plannedStartDate: phasePayload.plannedStartDate ? new Date(phasePayload.plannedStartDate) : null,
          plannedEndDate: phasePayload.plannedEndDate ? new Date(phasePayload.plannedEndDate) : null,
          status: 'TODO',
        },
      });

      for (const actPayload of phasePayload.activities) {
        await prisma.activity.create({
          data: {
            projectId: project.id,
            phaseId: phase.id,
            clientId: project.clientId,
            description: actPayload.description,
            publicTitle: actPayload.publicTitle ?? null,
            hours: actPayload.hours ?? 0,
            category: actPayload.category ?? null,
            mode: actPayload.mode ?? null,
            status: 'TODO',
            billingStatus: 'EXCLUDED',
            completed: false,
            plannedDate: null,
          },
        });
      }
    }

    if (project.startDate) {
      await calculatePhaseDates(project.id);
    }

    await sendWebhook({ entity: 'project', action: 'create', data: project });
    revalidatePath('/gestionale/dashboard/projects');
    return { ok: true, projectId: project.id };
  } catch (err) {
    console.error('Errore import progetto da JSON:', err);
    const message = err instanceof Error ? err.message : 'Errore durante la creazione del progetto.';
    return { ok: false, error: message };
  }
}

export async function generateProjectFromActivityWebhook(
  clientId: string,
  payload: unknown
): Promise<{ ok: boolean; projectId?: string; error?: string }> {
  const url = process.env.GENERA_PROGETTO_DA_ATTIVITA_URL;
  const username = process.env.GENERA_PROGETTO_DA_ATTIVITA_USERNAME;
  const password = process.env.GENERA_PROGETTO_DA_ATTIVITA_PASSWORD;
  if (!url || !username || !password) {
    return { ok: false, error: 'Configurazione webhook mancante (.env).' };
  }
  try {
    const auth = Buffer.from(`${username}:${password}`).toString('base64');
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Basic ${auth}`,
      },
      body: JSON.stringify(payload),
    });
    if (!response.ok) {
      return { ok: false, error: `Errore webhook: ${response.status} ${response.statusText}` };
    }
    const data = await response.json();
    const result = await importProjectFromJson(clientId, JSON.stringify(data));
    if (result.ok) {
      return { ok: true, projectId: result.projectId };
    } else {
      return { ok: false, error: result.error || 'Errore import progetto.' };
    }
  } catch (error) {
    const message = error instanceof Error ? error.message : 'Errore rete/webhook.';
    return { ok: false, error: message };
  }
}

export async function generateActivitySummaryFromWebhook(
  clientId: string,
  payload: unknown
): Promise<{ ok: boolean; text?: string; error?: string }> {
  const url = 'https://flow.edilnol.it/webhook/14a01a7c-1570-468c-b9b4-0637fa6caaf7';
  try {
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ clientId, payload }),
    });
    if (!response.ok) {
      return { ok: false, error: `Errore webhook: ${response.status} ${response.statusText}` };
    }
    const text = await response.text();
    return { ok: true, text };
  } catch (error) {
    const message = error instanceof Error ? error.message : 'Errore rete/webhook.';
    return { ok: false, error: message };
  }
}

export async function updateProject(
  id: string,
  prevState: State,
  formData: FormData,
) {
  const validatedFields = UpdateProject.safeParse({
    id,
    name: formData.get('name'),
    description: formData.get('description'),
    status: formData.get('status'),
    color: formData.get('color') || null,
    clientId: formData.get('clientId'),
    startDate: formData.get('startDate'),
    endDate: formData.get('endDate'),
    paymentMethod: formData.get('paymentMethod') || null,
    discountsJson: formData.get('discountsJson'),
    feesJson: formData.get('feesJson'),
  });

  if (!validatedFields.success) {
    return {
      errors: validatedFields.error.flatten().fieldErrors,
      message: 'Campi mancanti. Aggiornamento progetto non riuscito.',
    };
  }

  const data = validatedFields.data;

  const prismaWithNewModels = prisma as typeof prisma & { projectDiscount?: { deleteMany: unknown; createMany: unknown }; projectFee?: { deleteMany: unknown; createMany: unknown } };
  const hasNewProjectModels = typeof prismaWithNewModels.projectDiscount !== 'undefined';

  try {
    const project = await prisma.project.update({
      where: { id },
      data: {
        name: data.name,
        description: data.description || null,
        status: data.status,
        clientId: data.clientId,
        startDate: data.startDate ? new Date(data.startDate) : null,
        endDate: data.endDate ? new Date(data.endDate) : null,
        ...(hasNewProjectModels && {
          paymentMethod: data.paymentMethod === '' || data.paymentMethod == null ? null : data.paymentMethod,
        }),
      } as Parameters<typeof prisma.project.update>[0]['data'],
    });
    // Colore: aggiornamento via raw SQL per evitare errori con client Prisma in cache (Unknown argument `color`)
    await prisma.$executeRaw`UPDATE "Project" SET "color" = ${data.color || null}::text WHERE "id" = ${id}`;
    if (hasNewProjectModels) {
      const discounts = parseDiscountsJson(data.discountsJson);
      const fees = parseFeesJson(data.feesJson);
      await prismaWithNewModels.projectDiscount!.deleteMany({ where: { projectId: id } });
      await prismaWithNewModels.projectFee!.deleteMany({ where: { projectId: id } });
      if (discounts.length > 0) {
        await prismaWithNewModels.projectDiscount!.createMany({
          data: discounts.map((d, i) => ({
            projectId: id,
            title: d.title,
            description: d.description || null,
            amount: d.amount,
            order: i,
          })),
        });
      }
      if (fees.length > 0) {
        await prismaWithNewModels.projectFee!.createMany({
          data: fees.map((f, i) => ({
            projectId: id,
            name: f.name,
            amount: f.amount,
            period: f.period,
            order: i,
          })),
        });
      }
    }
    const itemsJsonRaw = formData.get('itemsJson') as string | null;
    if (itemsJsonRaw && itemsJsonRaw.trim() !== '') {
      try {
        const parsed = JSON.parse(itemsJsonRaw);
        if (Array.isArray(parsed)) {
          await updateProjectWhatYouGet(id, itemsJsonRaw);
        }
      } catch {}
    }

    await sendWebhook({
      entity: 'project',
      action: 'update',
      data: project,
    });

    // Ripianifica tutte le fasi a partire dalla nuova data inizio
    if (project.startDate) {
      await calculatePhaseDates(project.id);
    }
  } catch (error) {
    const msg = process.env.NODE_ENV === 'development' && error instanceof Error ? error.message : 'Errore database: aggiornamento progetto non riuscito.';
    return {
      message: msg,
    };
  }

  revalidatePath('/gestionale/dashboard/projects');
  revalidatePath(`/gestionale/dashboard/projects/${id}`);
  if (formData.get('noRedirect') === 'true') {
    return { message: 'Progetto aggiornato con successo.', errors: {} };
  }
  redirect('/dashboard/projects');
}

const PAYMENT_METHOD_VALUES = ['FIFTY_FIFTY', 'THIRTY_THIRTY_FORTY', 'ADVANCE', 'ON_DELIVERY'] as const;

/** Salva sconti, canoni e metodo di pagamento (per useFormState: prevState, formData) */
export async function updateProjectDiscountsAndFeesForm(
  _prevState: { ok: boolean; error?: string } | null,
  formData: FormData
) {
  const projectId = formData.get('projectId');
  if (!projectId || typeof projectId !== 'string') {
    return { ok: false, error: 'projectId mancante' };
  }
  const discountsJson = (formData.get('discountsJson') as string) ?? '[]';
  const feesJson = (formData.get('feesJson') as string) ?? '[]';
  const paymentMethod = (formData.get('paymentMethod') as string) || null;
  const result = await updateProjectDiscountsAndFees(projectId, discountsJson, feesJson, paymentMethod);
  return result;
}

export async function updateProjectDiscountsAndFees(
  projectId: string,
  discountsJson: string,
  feesJson: string,
  paymentMethod?: string | null
) {
  const prismaWithNewModels = prisma as typeof prisma & {
    projectDiscount?: { deleteMany: unknown; createMany: unknown };
    projectFee?: { deleteMany: unknown; createMany: unknown };
  };
  const discounts = parseDiscountsJson(discountsJson);
  const fees = parseFeesJson(feesJson);
  const paymentValue =
    paymentMethod && PAYMENT_METHOD_VALUES.includes(paymentMethod as (typeof PAYMENT_METHOD_VALUES)[number])
      ? paymentMethod
      : null;

  const paymentJson = JSON.stringify(paymentValue);
  const discountsStr = JSON.stringify(discounts);
  const feesStr = JSON.stringify(fees);
  try {
    await prisma.$executeRaw`
      UPDATE "Project"
      SET "pricingExtras" = COALESCE("pricingExtras", '{}'::jsonb) || jsonb_build_object(
        'paymentMethod', ${paymentJson}::jsonb,
        'discounts', ${discountsStr}::jsonb,
        'fees', ${feesStr}::jsonb
      )
      WHERE "id" = ${projectId}
    `;
  } catch (e) {
    const err = e as Error;
    console.error('updateProjectDiscountsAndFees: pricingExtras non salvato', err);
    revalidatePath('/gestionale/dashboard/projects');
    revalidatePath(`/gestionale/dashboard/projects/${projectId}`);
    const message =
      process.env.NODE_ENV === 'development' && err?.message
        ? `Salvataggio non riuscito: ${err.message}`
        : 'Salvataggio non riuscito. In terminale esegui: npx prisma generate && npx prisma db push';
    return {
      ok: false,
      error: message,
    };
  }

  // 3) Tabelle relazionali (se esistono dopo prisma generate + db push)
  try {
    if (typeof prismaWithNewModels.projectDiscount !== 'undefined') {
      await prismaWithNewModels.projectDiscount!.deleteMany({ where: { projectId } });
      await prismaWithNewModels.projectFee!.deleteMany({ where: { projectId } });
      if (discounts.length > 0) {
        await prismaWithNewModels.projectDiscount!.createMany({
          data: discounts.map((d, i) => ({
            projectId,
            title: d.title,
            description: d.description || null,
            amount: d.amount,
            order: i,
          })),
        });
      }
      if (fees.length > 0) {
        await prismaWithNewModels.projectFee!.createMany({
          data: fees.map((f, i) => ({
            projectId,
            name: f.name,
            amount: f.amount,
            period: f.period,
            optional: f.optional ?? false,
            order: i,
          })),
        });
      }
    }
  } catch (error) {
    console.error('Errore aggiornamento tabelle sconti/canoni:', error);
  }

  revalidatePath('/gestionale/dashboard/projects');
  revalidatePath(`/gestionale/dashboard/projects/${projectId}`);
  return { ok: true };
}

/** Salva la sezione “Cosa otterrai” (slides) nel progetto, usando pricingExtras.whatYouGet e campo top-level whatYouGet. */
export async function updateProjectWhatYouGetForm(
  _prevState: { ok: boolean; error?: string } | null,
  formData: FormData
) {
  const projectId = formData.get('projectId');
  const itemsJson = (formData.get('itemsJson') as string) ?? '[]';
  if (!projectId || typeof projectId !== 'string') {
    return { ok: false, error: 'projectId mancante' };
  }
  try {
    // Validazione minima: deve essere un array
    const parsed = JSON.parse(itemsJson);
    if (!Array.isArray(parsed)) {
      return { ok: false, error: 'itemsJson deve essere un array JSON' };
    }
  } catch (e) {
    return { ok: false, error: 'itemsJson non è un JSON valido' };
  }
  const result = await updateProjectWhatYouGet(projectId, itemsJson);
  return result;
}

export async function updateProjectWhatYouGet(projectId: string, itemsJson: string) {
  try {
    // Aggiorna sia il campo top-level che dentro pricingExtras per compatibilità
    await prisma.$executeRaw`
      UPDATE "Project"
      SET
        "whatYouGet" = ${itemsJson}::jsonb,
        "pricingExtras" = COALESCE("pricingExtras", '{}'::jsonb) || jsonb_build_object('whatYouGet', ${itemsJson}::jsonb)
      WHERE "id" = ${projectId}
    `;
  } catch (e) {
    const err = e as Error;
    console.error('updateProjectWhatYouGet: salvataggio non riuscito', err);
    revalidatePath('/gestionale/dashboard/projects');
    revalidatePath(`/gestionale/dashboard/projects/${projectId}`);
    const message =
      process.env.NODE_ENV === 'development' && err?.message
        ? `Salvataggio non riuscito: ${err.message}`
        : 'Salvataggio non riuscito. In terminale esegui: npx prisma generate && npx prisma db push';
    return { ok: false, error: message };
  }
  revalidatePath('/gestionale/dashboard/projects');
  revalidatePath(`/gestionale/dashboard/projects/${projectId}`);
  return { ok: true };
}

/** Ritorna { error } se il progetto non può essere eliminato (preventivo approvato), altrimenti elimina e redirect. */
export async function deleteProject(
  id: string,
  _prevState: { error?: string } | null,
  _formData: FormData
): Promise<{ error?: string }> {
  const project = await prisma.project.findUnique({
    where: { id },
    include: { acceptanceTokens: { select: { acceptedAt: true } } },
  });
  if (!project) {
    return { error: 'Progetto non trovato.' };
  }
  const hasApprovedQuote = project.acceptanceTokens?.some((t) => t.acceptedAt != null) ?? false;
  if (hasApprovedQuote) {
    return {
      error: 'Impossibile eliminare: il preventivo è stato già approvato dal cliente.',
    };
  }
  try {
    await prisma.project.delete({ where: { id } });
    revalidatePath('/gestionale/dashboard/projects');
    await sendWebhook({
      entity: 'project',
      action: 'delete',
      data: { id },
    });
  } catch (error) {
    console.error('Errore eliminazione progetto:', error);
    return { error: 'Errore durante l\'eliminazione del progetto.' };
  }
  redirect('/dashboard/projects');
}

export async function submitProjectAcceptanceAction(
  token: string,
  formData: FormData
) {
  const { submitProjectAcceptance } = await import('@/app/lib/data');
  const nome = (formData.get('nome') as string)?.trim();
  const cognome = (formData.get('cognome') as string)?.trim();
  const fiscalCode = (formData.get('fiscalCode') as string)?.trim();
  const luogo = (formData.get('luogo') as string)?.trim();
  const dataStr = formData.get('data') as string;
  if (!nome || !cognome || !fiscalCode || !luogo || !dataStr) {
    throw new Error('Compila tutti i campi.');
  }
  await submitProjectAcceptance(token, {
    nome,
    cognome,
    fiscalCode,
    luogo,
    data: new Date(dataStr),
  });
  revalidatePath(`/gestionale/preventivo/${token}`);
  revalidatePath(`/preventivo/${token}`);
}

/** Genera (o recupera) il link di accettazione preventivo per il progetto. Il token non cambierà più. */
export async function generateAcceptanceLinkAction(projectId: string): Promise<{ url: string }> {
  const { getOrCreateProjectAcceptanceToken } = await import('@/app/lib/data');
  const { getAcceptanceUrl } = await import('@/app/lib/utils');
  const record = await getOrCreateProjectAcceptanceToken(projectId);
  const url = getAcceptanceUrl(record.token);
  revalidatePath(`/dashboard/projects/${projectId}`);
  revalidatePath(`/gestionale/dashboard/projects/${projectId}`);
  return { url };
}

// ========== PHASE ACTIONS ==========

export async function createPhase(prevState: State, formData: FormData) {
  const validatedFields = CreatePhase.safeParse({
    projectId: formData.get('projectId'),
    name: formData.get('name'),
    description: formData.get('description'),
    plannedDuration: formData.get('plannedDuration'),
    order: formData.get('order') || '0',
    amount: formData.get('amount'),
    plannedStartDate: formData.get('plannedStartDate'),
    plannedEndDate: formData.get('plannedEndDate'),
    status: formData.get('status') || 'TODO',
  });

  if (!validatedFields.success) {
    return {
      errors: validatedFields.error.flatten().fieldErrors,
      message: 'Campi mancanti. Creazione fase non riuscita.',
    };
  }

  const data = validatedFields.data;

  try {
    const phase = await prisma.phase.create({
      data: {
        projectId: data.projectId,
        name: data.name,
        description: data.description || null,
        plannedDuration: data.plannedDuration,
        order: data.order,
        amount: data.amount ?? null,
        plannedStartDate: data.plannedStartDate ? new Date(data.plannedStartDate) : null,
        plannedEndDate: data.plannedEndDate ? new Date(data.plannedEndDate) : null,
        actualStartDate: null,
        actualEndDate: null,
        status: data.status,
      },
    });

    // Calcola date automatiche se richiesto
    const autoCalculate = formData.get('autoCalculateDates') === 'on';
    if (autoCalculate) {
      await calculatePhaseDates(phase.projectId);
    }

    revalidatePath(`/dashboard/projects/${data.projectId}`);
    return { message: 'Fase creata con successo.' };
  } catch (error) {
    return {
      message: 'Errore database: creazione fase non riuscita.',
    };
  }
}

export async function updatePhase(
  id: string,
  prevState: State,
  formData: FormData,
) {
  const validatedFields = UpdatePhase.safeParse({
    id,
    projectId: formData.get('projectId'),
    name: formData.get('name'),
    description: formData.get('description'),
    plannedDuration: formData.get('plannedDuration'),
    order: formData.get('order'),
    amount: formData.get('amount'),
    plannedStartDate: formData.get('plannedStartDate'),
    plannedEndDate: formData.get('plannedEndDate'),
    actualStartDate: formData.get('actualStartDate'),
    actualEndDate: formData.get('actualEndDate'),
    status: formData.get('status'),
  });

  if (!validatedFields.success) {
    return {
      errors: validatedFields.error.flatten().fieldErrors,
      message: 'Campi mancanti. Aggiornamento fase non riuscito.',
    };
  }

  const data = validatedFields.data;

  try {
    const phase = await prisma.phase.update({
      where: { id },
      data: {
        ...data,
        plannedStartDate: data.plannedStartDate ? new Date(data.plannedStartDate) : null,
        plannedEndDate: data.plannedEndDate ? new Date(data.plannedEndDate) : null,
        actualStartDate: data.actualStartDate ? new Date(data.actualStartDate) : null,
        actualEndDate: data.actualEndDate ? new Date(data.actualEndDate) : null,
      },
    });

    // Calcola date automatiche se richiesto
    const autoCalculate = formData.get('autoCalculateDates') === 'on';
    if (autoCalculate) {
      await calculatePhaseDates(phase.projectId);
    }

    revalidatePath(`/dashboard/projects/${data.projectId}`);
    if (formData.get('noRedirect') === 'true') {
      return { message: 'Fase aggiornata con successo.', errors: {} };
    }
    redirect(`/dashboard/projects/${data.projectId}`);
  } catch (error) {
    return {
      message: 'Errore database: aggiornamento fase non riuscito.',
    };
  }
}

export async function deletePhase(id: string, projectId: string) {
  try {
    await prisma.phase.delete({
      where: { id },
    });
    revalidatePath(`/dashboard/projects/${projectId}`);
    return { message: 'Fase eliminata con successo.' };
  } catch (error) {
    throw new Error('Errore database: eliminazione fase non riuscita.');
  }
}

// Funzione helper per calcolare date fasi sequenziali
async function calculatePhaseDates(projectId: string) {
  const project = await prisma.project.findUnique({
    where: { id: projectId },
    include: { phases: { orderBy: { order: 'asc' } } },
  });

  if (!project || !project.startDate) {
    return; // Non possiamo calcolare senza data inizio progetto
  }

  let currentDate = new Date(project.startDate);
  currentDate.setHours(0, 0, 0, 0);

  for (const phase of project.phases) {
    // Imposta data inizio
    await prisma.phase.update({
      where: { id: phase.id },
      data: {
        plannedStartDate: new Date(currentDate),
      },
    });

    // Calcola data fine (aggiungi giorni lavorativi)
    const endDate = new Date(currentDate);
    endDate.setDate(endDate.getDate() + phase.plannedDuration);
    
    await prisma.phase.update({
      where: { id: phase.id },
      data: {
        plannedEndDate: endDate,
      },
    });

    // La prossima fase inizia il giorno dopo la fine di questa
    currentDate = new Date(endDate);
    currentDate.setDate(currentDate.getDate() + 1);
  }

  // Aggiorna data fine progetto
  if (project.phases.length > 0) {
    const lastPhase = project.phases[project.phases.length - 1];
    const lastEndDate = await prisma.phase.findUnique({
      where: { id: lastPhase.id },
      select: { plannedEndDate: true },
    });
    
    if (lastEndDate?.plannedEndDate) {
      await prisma.project.update({
        where: { id: projectId },
        data: { endDate: lastEndDate.plannedEndDate },
      });
    }
  }
}

export async function updateActivityDescription(
  id: string,
  description: string,
) {
  try {
    if (!description || description.trim() === '') {
       return { success: false, message: 'La descrizione non può essere vuota.' };
    }
    await prisma.activity.update({
      where: { id },
      data: { description },
    });
    revalidatePath(`/gestionale/dashboard/activities/${id}`);
    revalidatePath('/gestionale/dashboard/activities');
    return { success: true, message: 'Descrizione aggiornata con successo.' };
  } catch (error) {
    console.error('Errore aggiornamento descrizione:', error);
    return {
      success: false,
      message: 'Errore durante l\'aggiornamento della descrizione.',
    };
  }
}

export async function updateActivityNotes(
  id: string,
  internalNotes: string | null,
) {
  try {
    await prisma.activity.update({
      where: { id },
      data: { internalNotes },
    });
    revalidatePath(`/gestionale/dashboard/activities/${id}`);
    revalidatePath('/gestionale/dashboard/activities');
    return { success: true, message: 'Note aggiornate con successo.' };
  } catch (error) {
    console.error('Errore aggiornamento note:', error);
    return {
      success: false,
      message: 'Errore durante l\'aggiornamento delle note.',
    };
  }
}

export type ChecklistItem = { id: string; label: string; done: boolean };

export async function updateActivityChecklist(
  id: string,
  checklist: ChecklistItem[],
) {
  try {
    await prisma.activity.update({
      where: { id },
      data: { checklist: checklist as unknown as Prisma.JsonArray },
    });
    revalidatePath(`/gestionale/dashboard/activities/${id}`);
    revalidatePath('/gestionale/dashboard/activities');
    return { success: true };
  } catch (error) {
    console.error('Errore aggiornamento checklist:', error);
    return { success: false };
  }
}

export async function addActivityComment(activityId: string, content: string) {
  const session = await auth();
  if (!session?.user?.id) {
    return { success: false, message: 'Utente non autenticato.' };
  }
  const trimmed = content?.trim();
  if (!trimmed) {
    return { success: false, message: 'Scrivi un commento.' };
  }
  try {
    await prisma.activityComment.create({
      data: {
        activityId,
        content: trimmed,
        userId: session.user.id,
      },
    });
    revalidatePath(`/gestionale/dashboard/activities/${activityId}`);
    return { success: true, message: 'Commento aggiunto.' };
  } catch (error) {
    console.error('Errore creazione commento:', error);
    return { success: false, message: 'Errore durante il salvataggio.' };
  }
}

export async function createLead(prevState: State, formData: FormData) {
  const validatedFields = CreateLead.safeParse({
    name: formData.get('name'),
    email: formData.get('email'),
    phone: formData.get('phone'),
    status: formData.get('status') || 'NEW',
    source: formData.get('source'),
  });

  if (!validatedFields.success) {
    return {
      errors: validatedFields.error.flatten().fieldErrors,
      message: 'Campi mancanti. Creazione lead non riuscita.',
    };
  }

  const data = validatedFields.data;

  try {
    const lead = await prisma.lead.create({
      data: {
        ...data,
      },
    });

    await sendWebhook({
      entity: 'lead',
      action: 'create',
      data: lead,
    });
  } catch (error) {
    return {
      message: 'Errore database: creazione lead non riuscita.',
    };
  }

  revalidatePath('/gestionale/dashboard/leads');
  redirect('/dashboard/leads');
}

export async function updateLead(
  id: string,
  prevState: State,
  formData: FormData,
) {
  const validatedFields = UpdateLead.safeParse({
    id,
    name: formData.get('name'),
    email: formData.get('email'),
    phone: formData.get('phone'),
    status: formData.get('status') || 'NEW',
    source: formData.get('source'),
  });

  if (!validatedFields.success) {
    return {
      errors: validatedFields.error.flatten().fieldErrors,
      message: 'Campi mancanti. Aggiornamento lead non riuscito.',
    };
  }

  const data = validatedFields.data;

  try {
    const lead = await prisma.lead.update({
      where: { id },
      data: {
        ...data,
      },
    });

    await sendWebhook({
      entity: 'lead',
      action: 'update',
      data: lead,
    });
  } catch (error) {
    return { message: 'Errore database: aggiornamento lead non riuscito.' };
  }

  revalidatePath('/gestionale/dashboard/leads');
  redirect('/dashboard/leads');
}

export async function deleteLead(id: string, _formData: FormData) {
  try {
    await prisma.lead.delete({
      where: { id },
    });
    revalidatePath('/gestionale/dashboard/leads');
  } catch (error) {
    console.error('Errore database: eliminazione lead non riuscita.', error);
    throw new Error('Errore database: eliminazione lead non riuscita.');
  }

  await sendWebhook({
    entity: 'lead',
    action: 'delete',
    data: { id },
  });
}

export async function createOpportunity(
  prevState: State,
  formData: FormData,
) {
  const validatedFields = CreateOpportunity.safeParse({
    title: formData.get('title'),
    value: formData.get('value'),
    status: formData.get('status') || 'OPEN',
    clientId: formData.get('clientId'),
    leadId: formData.get('leadId'),
    description: formData.get('description'),
  });

  if (!validatedFields.success) {
    return {
      errors: validatedFields.error.flatten().fieldErrors,
      message: 'Campi mancanti. Creazione opportunità non riuscita.',
    };
  }

  const data = validatedFields.data;

  try {
    const opportunity = await prisma.opportunity.create({
      data: {
        ...data,
        clientId: data.clientId || null,
        leadId: data.leadId || null,
      },
      include: {
        client: {
          select: {
            name: true,
          },
        },
        lead: {
          select: {
            name: true,
          },
        },
      },
    });

    await sendWebhook({
      entity: 'opportunity',
      action: 'create',
      data: opportunity,
    });

    await sendOpportunityCreatedWhatsAppNotification({
      id: opportunity.id,
      title: opportunity.title,
      value: opportunity.value,
      status: opportunity.status,
      clientName: opportunity.client?.name,
      leadName: opportunity.lead?.name,
      description: opportunity.description,
      source: 'Gestionale',
    });
  } catch (error) {
    return {
      message: 'Errore database: creazione opportunità non riuscita.',
    };
  }

  revalidatePath('/gestionale/dashboard/opportunities');
  redirect('/dashboard/opportunities?created=1');
}

export async function updateOpportunity(
  id: string,
  prevState: State,
  formData: FormData,
) {
  const validatedFields = UpdateOpportunity.safeParse({
    id,
    title: formData.get('title'),
    value: formData.get('value'),
    status: formData.get('status') || 'OPEN',
    clientId: formData.get('clientId'),
    leadId: formData.get('leadId'),
    description: formData.get('description'),
  });

  if (!validatedFields.success) {
    return {
      errors: validatedFields.error.flatten().fieldErrors,
      message: 'Campi mancanti. Aggiornamento opportunità non riuscito.',
    };
  }

  const data = validatedFields.data;

  try {
    const opportunity = await prisma.opportunity.update({
      where: { id },
      data: {
        ...data,
        clientId: data.clientId || null,
        leadId: data.leadId || null,
      },
    });

    await sendWebhook({
      entity: 'opportunity',
      action: 'update',
      data: opportunity,
    });
  } catch (error) {
    return {
      message: 'Errore database: aggiornamento opportunità non riuscito.',
    };
  }

  revalidatePath('/gestionale/dashboard/opportunities');
  redirect('/dashboard/opportunities');
}

export async function deleteOpportunity(
  id: string,
  _formData: FormData,
) {
  try {
    await prisma.opportunity.delete({
      where: { id },
    });
    revalidatePath('/gestionale/dashboard/opportunities');
  } catch (error) {
    console.error(
      'Errore database: eliminazione opportunità non riuscita.',
      error,
    );
    throw new Error('Errore database: eliminazione opportunità non riuscita.');
  }

  await sendWebhook({
    entity: 'opportunity',
    action: 'delete',
    data: { id },
  });
}

export async function updateOpportunityStatus(
  id: string,
  status: 'OPEN' | 'WON' | 'LOST',
) {
  try {
    const opportunity = await prisma.opportunity.update({
      where: { id },
      data: { status },
    });

    await sendWebhook({
      entity: 'opportunity',
      action: 'updateStatus',
      data: opportunity,
    });
  } catch (error) {
    console.error(
      'Errore database: aggiornamento stato opportunità non riuscito.',
      error,
    );
    throw new Error('Errore database: aggiornamento stato opportunità non riuscito.');
  }

  revalidatePath('/gestionale/dashboard/opportunities');
  revalidatePath('/gestionale/dashboard/opportunities/kanban');
}

export async function rescheduleActivity(
  id: string,
  dateISO: string,
  hour: number,
  timezoneOffset: number = 0,
) {
  try {
    const adjustDate = (date: Date | null) => {
      if (!date) return null;
      if (timezoneOffset === 0) return date;
      return new Date(date.getTime() + timezoneOffset * 60 * 1000);
    };

    const activity = await prisma.activity.findUnique({
      where: { id },
      select: {
        plannedDate: true,
        plannedStartTime: true,
        plannedEndTime: true,
        date: true,
        startTime: true,
        endTime: true,
        hours: true,
      },
    });

    if (!activity) {
      throw new Error('Attività non trovata.');
    }

    const baseDate = new Date(dateISO);
    baseDate.setHours(0, 0, 0, 0);

    const plannedStartTime = new Date(baseDate);
    plannedStartTime.setHours(hour, 0, 0, 0);

    let plannedEndTime: Date | null = null;

    if (activity.plannedStartTime && activity.plannedEndTime) {
      const durationMs =
        activity.plannedEndTime.getTime() -
        activity.plannedStartTime.getTime();
      plannedEndTime = new Date(plannedStartTime.getTime() + durationMs);
    } else if (activity.startTime && activity.endTime) {
      const durationMs =
        activity.endTime.getTime() - activity.startTime.getTime();
      plannedEndTime = new Date(plannedStartTime.getTime() + durationMs);
    } else if (activity.hours && activity.hours > 0) {
      const durationMs = activity.hours * 60 * 60 * 1000;
      plannedEndTime = new Date(plannedStartTime.getTime() + durationMs);
    } else {
      const durationMs = 60 * 60 * 1000;
      plannedEndTime = new Date(plannedStartTime.getTime() + durationMs);
    }

    const updated = await prisma.activity.update({
      where: { id },
      data: {
        plannedDate: adjustDate(baseDate),
        plannedStartTime: adjustDate(plannedStartTime),
        plannedEndTime: adjustDate(plannedEndTime),
      },
    });

    await sendWebhook({
      entity: 'activity',
      action: 'update',
      data: updated,
    });
  } catch (error) {
    console.error(
      'Errore database: ripianificazione attività non riuscita.',
      error,
    );
    throw new Error('Errore database: ripianificazione attività non riuscita.');
  }

  revalidatePath('/gestionale/dashboard');
  revalidatePath('/gestionale/dashboard/activities');
}

export async function clearActivityPlanning(id: string) {
  try {
    await prisma.activity.update({
      where: { id },
      data: {
        plannedDate: null,
        plannedStartTime: null,
        plannedEndTime: null,
      },
    });
    revalidatePath('/gestionale/dashboard');
    revalidatePath('/gestionale/dashboard/activities');
  } catch (error) {
    console.error('Errore annullamento pianificazione:', error);
    throw new Error('Errore durante l’annullamento della pianificazione.');
  }
}

export async function updateActivityDurationFromPlanner(
  id: string,
  dateISO: string,
  hour: number,
  durationHours: number,
  timezoneOffset: number = 0,
) {
  try {
    const adjustDate = (date: Date | null) => {
      if (!date) return null;
      if (timezoneOffset === 0) return date;
      return new Date(date.getTime() + timezoneOffset * 60 * 1000);
    };

    const baseDate = new Date(dateISO);
    baseDate.setHours(0, 0, 0, 0);

    const plannedStartTime = new Date(baseDate);
    plannedStartTime.setHours(hour, 0, 0, 0);

    const effectiveDuration = durationHours <= 0.5 ? 0.5 : durationHours;
    const durationMs = effectiveDuration * 60 * 60 * 1000;
    const plannedEndTime = new Date(plannedStartTime.getTime() + durationMs);

    const updated = await prisma.activity.update({
      where: { id },
      data: {
        plannedDate: adjustDate(baseDate),
        plannedStartTime: adjustDate(plannedStartTime),
        plannedEndTime: adjustDate(plannedEndTime),
        hours: effectiveDuration,
      },
    });

    await sendWebhook({
      entity: 'activity',
      action: 'update',
      data: updated,
    });
  } catch (error) {
    console.error(
      'Errore database: aggiornamento durata attività non riuscito.',
      error,
    );
    throw new Error(
      'Errore database: aggiornamento durata attività non riuscito.',
    );
  }

  revalidatePath('/gestionale/dashboard');
  revalidatePath('/gestionale/dashboard/activities');
}

export async function createArticle(prevState: State, formData: FormData) {
  const validatedFields = CreateArticle.safeParse({
    name: formData.get('name'),
    price: formData.get('price'),
    recurrence: formData.get('recurrence') || 'NONE',
    description: formData.get('description'),
  });

  if (!validatedFields.success) {
    return {
      errors: validatedFields.error.flatten().fieldErrors,
      message: 'Campi mancanti. Creazione articolo non riuscita.',
    };
  }

  const data = validatedFields.data;

  try {
    const article = await prisma.article.create({
      data: {
        ...data,
      },
    });

    await sendWebhook({
      entity: 'article',
      action: 'create',
      data: article,
    });
  } catch (error) {
    return {
      message: 'Errore database: creazione articolo non riuscita.',
    };
  }

  revalidatePath('/gestionale/dashboard/articles');
  redirect('/dashboard/articles');
}

export async function updateArticle(
  id: string,
  prevState: State,
  formData: FormData,
) {
  const validatedFields = UpdateArticle.safeParse({
    id,
    name: formData.get('name'),
    price: formData.get('price'),
    recurrence: formData.get('recurrence') || 'NONE',
    description: formData.get('description'),
  });

  if (!validatedFields.success) {
    return {
      errors: validatedFields.error.flatten().fieldErrors,
      message: 'Campi mancanti. Aggiornamento articolo non riuscito.',
    };
  }

  const data = validatedFields.data;

  try {
    const article = await prisma.article.update({
      where: { id },
      data: {
        ...data,
      },
    });

    await sendWebhook({
      entity: 'article',
      action: 'update',
      data: article,
    });
  } catch (error) {
    return {
      message: 'Errore database: aggiornamento articolo non riuscito.',
    };
  }

  revalidatePath('/gestionale/dashboard/articles');
  redirect('/dashboard/articles');
}

export async function deleteArticle(
  id: string,
  _formData: FormData,
) {
  try {
    await prisma.article.delete({
      where: { id },
    });
    revalidatePath('/gestionale/dashboard/articles');
  } catch (error) {
    console.error(
      'Errore database: eliminazione articolo non riuscita.',
      error,
    );
    throw new Error('Errore database: eliminazione articolo non riuscita.');
  }

  await sendWebhook({
    entity: 'article',
    action: 'delete',
    data: { id },
  });
}

export async function createDocument(prevState: State, formData: FormData) {
  const validatedFields = CreateDocument.safeParse({
    date: formData.get('date'),
    clientId: formData.get('clientId'),
    status: formData.get('status') || 'DRAFT',
    type: formData.get('type') || 'CONTRACT',
    autoRenew: formData.get('autoRenew') === 'on',
    notes: formData.get('notes'),
    adminReference: formData.get('adminReference'),
  });

  if (!validatedFields.success) {
    return {
      errors: validatedFields.error.flatten().fieldErrors,
      message: 'Campi mancanti. Creazione documento non riuscita.',
    };
  }

  const data = validatedFields.data;

  const baseDate = new Date(data.date);
  const year = baseDate.getFullYear();
  const prefix = `${year}-`;

  const lastForYear = await prisma.document.findFirst({
    where: {
      number: {
        startsWith: prefix,
      },
    },
    orderBy: {
      number: 'desc',
    },
  });

  let nextSequence = 1;

  if (lastForYear) {
    const suffix = lastForYear.number.slice(prefix.length);
    const parsed = parseInt(suffix, 10);
    if (!Number.isNaN(parsed) && parsed >= 0) {
      nextSequence = parsed + 1;
    }
  }

  const generatedNumber = `${prefix}${String(nextSequence).padStart(4, '0')}`;

  let newDocument;
  try {
    newDocument = await prisma.document.create({
      data: {
        ...data,
        number: generatedNumber,
        date: baseDate,
      }
    });

    await sendWebhook({
      entity: 'document',
      action: 'create',
      data: newDocument,
    });
  } catch (error) {
    return {
      message: 'Errore database: creazione documento non riuscita.',
    };
  }
  
  revalidatePath('/gestionale/dashboard/documents');
  redirect(`/gestionale/dashboard/documents/${newDocument.id}/edit`);
}

export async function createServiceLine(prevState: State, formData: FormData) {
  const validatedFields = CreateServiceLine.safeParse({
    documentId: formData.get('documentId'),
    articleId: formData.get('articleId') || undefined,
    type: formData.get('type'),
    description: formData.get('description'),
    recurrence: formData.get('recurrence'),
    startDate: formData.get('startDate'),
    endDate: formData.get('endDate') || undefined,
    price: formData.get('price'),
    billingMethod: formData.get('billingMethod'),
    status: formData.get('status') || 'ACTIVE',
  });

  if (!validatedFields.success) {
    return {
      errors: validatedFields.error.flatten().fieldErrors,
      message: 'Campi mancanti. Creazione riga non riuscita.',
    };
  }

  const data = validatedFields.data;
  const documentId = data.documentId;

  try {
    const serviceLine = await prisma.serviceLine.create({
      data: {
        ...data,
        startDate: new Date(data.startDate),
        endDate: data.endDate ? new Date(data.endDate) : null,
      }
    });

    await sendWebhook({
      entity: 'serviceLine',
      action: 'create',
      data: serviceLine,
    });
  } catch (error) {
    console.error('Detailed ServiceLine Create Error:', error);
    return {
      message: `Errore database: creazione riga non riuscita. ${error instanceof Error ? error.message : String(error)}`,
    };
  }

  revalidatePath(`/gestionale/dashboard/documents/${documentId}`);
  redirect(`/gestionale/dashboard/documents/${documentId}`);
}

export async function updateDocument(
  id: string,
  prevState: State,
  formData: FormData,
) {
  const validatedFields = UpdateDocument.safeParse({
    id: id,
    number: formData.get('number'),
    date: formData.get('date'),
    clientId: formData.get('clientId'),
    status: formData.get('status'),
    type: formData.get('type'),
    autoRenew: formData.get('autoRenew') === 'on',
    notes: formData.get('notes'),
    adminReference: formData.get('adminReference'),
  });

  if (!validatedFields.success) {
    return {
      errors: validatedFields.error.flatten().fieldErrors,
      message: 'Campi mancanti. Aggiornamento documento non riuscito.',
    };
  }

  const data = validatedFields.data;

  try {
    const document = await prisma.document.update({
      where: { id },
      data: {
        ...data,
        date: new Date(data.date),
      },
    });

    await sendWebhook({
      entity: 'document',
      action: 'update',
      data: document,
    });
  } catch (error) {
    return {
      message: 'Errore database: aggiornamento documento non riuscito.',
    };
  }

  revalidatePath('/gestionale/dashboard/documents');
  redirect('/dashboard/documents');
}

export async function deleteDocument(id: string) {
  try {
    await prisma.document.delete({
      where: { id },
    });
    revalidatePath('/gestionale/dashboard/documents');
  } catch (error) {
    throw new Error('Errore database: eliminazione documento non riuscita.');
  }

  await sendWebhook({
    entity: 'document',
    action: 'delete',
    data: { id },
  });
}

export async function updateServiceLine(
  id: string,
  prevState: State,
  formData: FormData,
) {
  const validatedFields = UpdateServiceLine.safeParse({
    id: id,
    documentId: formData.get('documentId'),
    articleId: formData.get('articleId') || undefined,
    type: formData.get('type'),
    description: formData.get('description'),
    recurrence: formData.get('recurrence'),
    startDate: formData.get('startDate'),
    endDate: formData.get('endDate') || undefined,
    price: formData.get('price'),
    includedHours: formData.get('includedHours'),
    billingMethod: formData.get('billingMethod'),
    status: formData.get('status'),
  });

  if (!validatedFields.success) {
    return {
      errors: validatedFields.error.flatten().fieldErrors,
      message: 'Campi mancanti. Aggiornamento riga non riuscito.',
    };
  }

  const data = validatedFields.data;

  try {
    const serviceLine = await prisma.serviceLine.update({
      where: { id },
      data: {
        ...data,
        startDate: new Date(data.startDate),
        endDate: data.endDate ? new Date(data.endDate) : null,
      },
    });

    await sendWebhook({
      entity: 'serviceLine',
      action: 'update',
      data: serviceLine,
    });
  } catch (error) {
    return {
      message: 'Errore database: aggiornamento riga non riuscito.',
    };
  }

  revalidatePath(`/gestionale/dashboard/documents/${data.documentId}/edit`);
  redirect(`/gestionale/dashboard/documents/${data.documentId}/edit`);
}

export async function deleteServiceLine(id: string, documentId: string) {
  try {
    await prisma.serviceLine.delete({
      where: { id },
    });
    revalidatePath(`/gestionale/dashboard/documents/${documentId}/edit`);
  } catch (error) {
     throw new Error('Errore database: eliminazione riga non riuscita.');
  }

  await sendWebhook({
    entity: 'serviceLine',
    action: 'delete',
    data: { id, documentId },
  });
}

export async function createActivity(prevState: State, formData: FormData) {
  const validatedFields = CreateActivity.safeParse({
    description: formData.get('description'),
    publicTitle: formData.get('publicTitle'),
    date: formData.get('date'),
    plannedDate: formData.get('plannedDate'),
    plannedStartTime: formData.get('plannedStartTime'),
    plannedEndTime: formData.get('plannedEndTime'),
    startTime: formData.get('startTime'),
    endTime: formData.get('endTime'),
    hours: formData.get('hours'),
    status: formData.get('status') || 'TODO',
    billingStatus: formData.get('billingStatus') || 'TO_BILL',
    billable: formData.get('billable') === 'on',
    category: formData.get('category'),
    mode: formData.get('mode'),
    clientId: formData.get('clientId'),
    documentId: formData.get('documentId') || undefined,
    serviceLineId: formData.get('serviceLineId') || undefined,
    invoiceId: formData.get('invoiceId') || undefined,
    userId: formData.get('userId') || undefined,
    contactId: formData.get('contactId') || undefined,
    projectId: formData.get('projectId') || undefined,
    internalNotes: formData.get('internalNotes'),
  });

  if (!validatedFields.success) {
    return {
      errors: validatedFields.error.flatten().fieldErrors,
      message: 'Campi mancanti. Creazione attività non riuscita.',
    };
  }

  const data = validatedFields.data;

  const timezoneOffsetRaw = formData.get('timezoneOffset');
  const timezoneOffset = timezoneOffsetRaw ? Number(timezoneOffsetRaw) : 0;

  const adjustDate = (date: Date | null) => {
    if (!date) return null;
    if (timezoneOffset === 0) return date;
    return new Date(date.getTime() + timezoneOffset * 60 * 1000);
  };

  const hourlyRate = await resolveHourlyRateForClient(data.clientId);
  const calculatedAmount = data.hours * hourlyRate;

  if (data.status === 'DONE') {
    const hasHours = data.hours > 0;
    const hasTimes = !!data.startTime && !!data.endTime;
    
    // Amount is calculated now, so checking if amount is > 0 is basically checking if hours > 0 (unless rate is 0)
    // But strict check:
    const hasAmount = calculatedAmount > 0;

    if (!hasHours && !hasTimes && !hasAmount) {
      return {
        errors: {
          hours: [
            'Per le attività completate indicare le ore, l’orario di inizio/fine o un importo',
          ],
        },
        message: 'Campi mancanti. Creazione attività non riuscita.',
      };
    }
  }

  try {
    const isProjectActivity = !!(data.projectId || data.phaseId);
    const activity = await prisma.activity.create({
      data: {
        ...data,
        date: new Date(data.date),
        plannedDate: data.plannedDate ? new Date(data.plannedDate) : null,
        plannedStartTime:
          data.plannedDate && data.plannedStartTime
            ? adjustDate(new Date(`${data.plannedDate}T${data.plannedStartTime}`))
            : null,
        plannedEndTime:
          data.plannedDate && data.plannedEndTime
            ? adjustDate(new Date(`${data.plannedDate}T${data.plannedEndTime}`))
            : null,
        startTime: data.startTime ? adjustDate(new Date(`${data.date}T${data.startTime}`)) : null,
        endTime: data.endTime ? adjustDate(new Date(`${data.date}T${data.endTime}`)) : null,
        publicTitle: data.publicTitle || null,
        category: data.category || null,
        mode: data.mode || null,
        clientId: data.clientId,
        documentId: data.documentId || null,
        serviceLineId: data.serviceLineId || null,
        invoiceId: data.invoiceId || null,
        userId: data.userId || null,
        contactId: data.contactId || null,
        projectId: data.projectId || null,
        phaseId: data.phaseId || null,
        billingStatus: isProjectActivity ? 'EXCLUDED' : (data.billingStatus || 'TO_BILL'),
        hourlyRateSnapshot: hourlyRate,
        amountSnapshot: calculatedAmount,
      },
    });

    await sendWebhook({
      entity: 'activity',
      action: 'create',
      data: activity,
    });
  } catch (error) {
    return {
      message: 'Errore database: creazione attività non riuscita.',
    };
  }
  revalidatePath('/gestionale/dashboard/activities');
  revalidatePath('/gestionale/dashboard/activities/kanban');
  redirect('/dashboard/activities/kanban');
}

export async function updateActivity(
  id: string,
  prevState: State,
  formData: FormData,
) {
  const validatedFields = UpdateActivity.safeParse({
    id,
    description: formData.get('description'),
    publicTitle: formData.get('publicTitle'),
    date: formData.get('date'),
    plannedDate: formData.get('plannedDate'),
    plannedStartTime: formData.get('plannedStartTime'),
    plannedEndTime: formData.get('plannedEndTime'),
    startTime: formData.get('startTime'),
    endTime: formData.get('endTime'),
    hours: formData.get('hours'),
    status: formData.get('status'),
    billingStatus: formData.get('billingStatus'),
    billable: formData.get('billable') === 'on',
    category: formData.get('category'),
    mode: formData.get('mode'),
    clientId: formData.get('clientId'),
    documentId: formData.get('documentId') || undefined,
    serviceLineId: formData.get('serviceLineId') || undefined,
    invoiceId: formData.get('invoiceId') || undefined,
    userId: formData.get('userId') || undefined,
    contactId: formData.get('contactId') || undefined,
    projectId: formData.get('projectId') || undefined,
    phaseId: formData.get('phaseId') || undefined,
    internalNotes: formData.get('internalNotes'),
  });

  if (!validatedFields.success) {
    return {
      errors: validatedFields.error.flatten().fieldErrors,
      message: 'Campi mancanti. Aggiornamento attività non riuscito.',
    };
  }

  const data = validatedFields.data;

  const hourlyRate = await resolveHourlyRateForClient(data.clientId);
  const calculatedAmount = data.hours * hourlyRate;

  // Use calculated amount if we have hours, otherwise if explicit amount is set (flat fee?) use that?
  // But strictly following "calculate with cost in client registry":
  let amount = calculatedAmount;
  
  // If hours are 0, maybe allow amountSnapshot to persist if it was manually set?
  // But user wants to remove hourly rate from activities.
  // If I have 0 hours, amount is 0.
  // If the user wants a fixed price activity with 0 hours, they can't do it if I enforce hours * rate.
  // But let's assume standard time & material for now as per "tariffa oraria".
  
  if (data.status === 'DONE') {
    const hasHours = data.hours > 0;
    const hasTimes = !!data.startTime && !!data.endTime;
    // Amount is calculated from hours, so checking amount > 0 is redundant if we check hours > 0
    // unless rate is 0.
    const hasAmount = amount > 0;

    if (!hasHours && !hasTimes && !hasAmount) {
      return {
        errors: {
          hours: [
            'Per le attività completate indicare le ore, l’orario di inizio/fine o un importo',
          ],
        },
        message: 'Campi mancanti. Aggiornamento attività non riuscito.',
      };
    }
  }

  const timezoneOffsetRaw = formData.get('timezoneOffset');
  const timezoneOffset = timezoneOffsetRaw ? Number(timezoneOffsetRaw) : 0;

  const adjustDate = (date: Date | null) => {
    if (!date) return null;
    if (timezoneOffset === 0) return date;
    return new Date(date.getTime() + timezoneOffset * 60 * 1000);
  };

  try {
    const activity = await prisma.activity.update({
      where: { id },
      data: {
        ...data,
        date: adjustDate(new Date(data.date))!,
        plannedDate: adjustDate(data.plannedDate ? new Date(data.plannedDate) : null),
        plannedStartTime:
          data.plannedDate && data.plannedStartTime
            ? adjustDate(new Date(`${data.plannedDate}T${data.plannedStartTime}`))
            : null,
        plannedEndTime:
          data.plannedDate && data.plannedEndTime
            ? adjustDate(new Date(`${data.plannedDate}T${data.plannedEndTime}`))
            : null,
        startTime: data.startTime ? adjustDate(new Date(`${data.date}T${data.startTime}`)) : null,
        endTime: data.endTime ? adjustDate(new Date(`${data.date}T${data.endTime}`)) : null,
        publicTitle: data.publicTitle || null,
        category: data.category || null,
        mode: data.mode || null,
        clientId: data.clientId,
        documentId: data.documentId || null,
        serviceLineId: data.serviceLineId || null,
        invoiceId: data.invoiceId || null,
        userId: data.userId || null,
        contactId: data.contactId || null,
        projectId: data.projectId || null,
        phaseId: data.phaseId || null,
        hourlyRateSnapshot: hourlyRate,
        amountSnapshot: amount,
      },
    });

    await sendWebhook({
      entity: 'activity',
      action: 'update',
      data: activity,
    });
  } catch (error) {
    console.error('Update activity error:', error);
    return {
      message: 'Errore database: aggiornamento attività non riuscito.',
    };
  }
  revalidatePath('/gestionale/dashboard/activities');
  revalidatePath('/gestionale/dashboard/activities/kanban');
  const noRedirect = formData.get('noRedirect');
  if (noRedirect) {
    const projectId = formData.get('projectId');
    if (projectId && typeof projectId === 'string') {
      revalidatePath(`/gestionale/dashboard/projects/${projectId}`);
    }
    return { message: null, errors: {} };
  }
  redirect('/dashboard/activities/kanban');
}

export async function createActivityFromPlanner(
  prevState: State,
  formData: FormData,
): Promise<State> {
  const validatedFields = CreateActivity.safeParse({
    description: formData.get('description'),
    publicTitle: formData.get('publicTitle'),
    date: formData.get('date'),
    plannedDate: formData.get('plannedDate'),
    plannedStartTime: formData.get('plannedStartTime'),
    plannedEndTime: formData.get('plannedEndTime'),
    startTime: formData.get('startTime'),
    endTime: formData.get('endTime'),
    hours: formData.get('hours'),
    status: formData.get('status') || 'TODO',
    billingStatus: formData.get('billingStatus') || 'TO_BILL',
    billable: formData.get('billable') === 'on',
    category:
      typeof formData.get('category') === 'string'
        ? formData.get('category')
        : '',
    mode:
      typeof formData.get('mode') === 'string' ? formData.get('mode') : '',
    clientId: formData.get('clientId'),
    documentId: formData.get('documentId') || undefined,
    serviceLineId: formData.get('serviceLineId') || undefined,
    invoiceId: formData.get('invoiceId') || undefined,
    projectId: formData.get('projectId') || undefined,
    internalNotes:
      typeof formData.get('internalNotes') === 'string'
        ? formData.get('internalNotes')
        : undefined,
  });

  if (!validatedFields.success) {
    return {
      errors: validatedFields.error.flatten().fieldErrors,
      message: 'Campi mancanti. Creazione attività non riuscita.',
    };
  }

  const data = validatedFields.data;

  const timezoneOffsetRaw = formData.get('timezoneOffset');
  const timezoneOffset = timezoneOffsetRaw ? Number(timezoneOffsetRaw) : 0;

  const adjustDate = (date: Date | null) => {
    if (!date) return null;
    if (timezoneOffset === 0) return date;
    return new Date(date.getTime() + timezoneOffset * 60 * 1000);
  };

  const hourlyRate = await resolveHourlyRateForClient(data.clientId);
  const calculatedAmount = data.hours * hourlyRate;

  if (data.status === 'DONE') {
    const hasHours = data.hours > 0;
    const hasTimes = !!data.startTime && !!data.endTime;
    const hasAmount = calculatedAmount > 0;

    if (!hasHours && !hasTimes && !hasAmount) {
      return {
        errors: {
          hours: [
            'Per le attività completate indicare le ore, l’orario di inizio/fine o un importo',
          ],
        },
        message: 'Campi mancanti. Creazione attività non riuscita.',
      };
    }
  }

  try {
    const isProjectActivity = !!data.projectId;
    const activity = await prisma.activity.create({
      data: {
        ...data,
        date: adjustDate(new Date(data.date))!,
        plannedDate: adjustDate(data.plannedDate ? new Date(data.plannedDate) : null),
        plannedStartTime:
          data.plannedDate && data.plannedStartTime
            ? adjustDate(new Date(`${data.plannedDate}T${data.plannedStartTime}`))
            : null,
        plannedEndTime:
          data.plannedDate && data.plannedEndTime
            ? adjustDate(new Date(`${data.plannedDate}T${data.plannedEndTime}`))
            : null,
        startTime: data.startTime ? adjustDate(new Date(`${data.date}T${data.startTime}`)) : null,
        endTime: data.endTime ? adjustDate(new Date(`${data.date}T${data.endTime}`)) : null,
        publicTitle: data.publicTitle || null,
        category: data.category || null,
        mode: data.mode || null,
        clientId: data.clientId,
        documentId: data.documentId || null,
        serviceLineId: data.serviceLineId || null,
        invoiceId: data.invoiceId || null,
        projectId: data.projectId || null,
        billingStatus: isProjectActivity ? 'EXCLUDED' : (data.billingStatus || 'TO_BILL'),
        hourlyRateSnapshot: hourlyRate,
        amountSnapshot: calculatedAmount,
      },
    });

    await sendWebhook({
      entity: 'activity',
      action: 'create',
      data: activity,
    });
  } catch (error) {
    return {
      message: 'Errore database: creazione attività non riuscita.',
    };
  }

  revalidatePath('/gestionale/dashboard');
  revalidatePath('/gestionale/dashboard/activities');
  revalidatePath('/gestionale/dashboard/activities/kanban');

  return {
    message: 'CREATED',
    errors: {},
  };
}

export async function updateActivityFromPlanner(
  id: string,
  prevState: State,
  formData: FormData,
): Promise<State> {
  console.log('updateActivityFromPlanner payload:', Object.fromEntries(formData.entries()));
  const validatedFields = UpdateActivity.safeParse({
    id,
    description: formData.get('description'),
    publicTitle: formData.get('publicTitle'),
    date: formData.get('date'),
    plannedDate: formData.get('plannedDate'),
    plannedStartTime: formData.get('plannedStartTime'),
    plannedEndTime: formData.get('plannedEndTime'),
    startTime: formData.get('startTime'),
    endTime: formData.get('endTime'),
    hours: formData.get('hours'),
    status: formData.get('status'),
    billingStatus: formData.get('billingStatus'),
    billable: formData.get('billable') === 'on',
    category: formData.get('category'),
    mode: formData.get('mode'),
    clientId: formData.get('clientId'),
    userId: formData.get('userId') || undefined,
    contactId: formData.get('contactId') || undefined,
    documentId: formData.get('documentId') || undefined,
    serviceLineId: formData.get('serviceLineId') || undefined,
    invoiceId: formData.get('invoiceId') || undefined,
    projectId: formData.get('projectId') || undefined,
    phaseId: formData.get('phaseId') || undefined,
    internalNotes:
      typeof formData.get('internalNotes') === 'string'
        ? formData.get('internalNotes')
        : undefined,
  });

  if (!validatedFields.success) {
    return {
      errors: validatedFields.error.flatten().fieldErrors,
      message: 'Campi mancanti. Aggiornamento attività non riuscito.',
    };
  }

  const data = validatedFields.data;

  const timezoneOffsetRaw = formData.get('timezoneOffset');
  const timezoneOffset = timezoneOffsetRaw ? Number(timezoneOffsetRaw) : 0;

  const adjustDate = (date: Date | null) => {
    if (!date) return null;
    if (timezoneOffset === 0) return date;
    return new Date(date.getTime() + timezoneOffset * 60 * 1000);
  };

  const hourlyRate = await resolveHourlyRateForClient(data.clientId);
  const calculatedAmount = data.hours * hourlyRate;

  if (data.status === 'DONE') {
    const hasHours = data.hours > 0;
    const hasTimes = !!data.startTime && !!data.endTime;
    const hasAmount = calculatedAmount > 0;

    if (!hasHours && !hasTimes && !hasAmount) {
      return {
        errors: {
          hours: [
            'Per le attività completate indicare le ore, l’orario di inizio/fine o un importo',
          ],
        },
        message: 'Campi mancanti. Aggiornamento attività non riuscito.',
      };
    }
  }

  try {
    const activity = await prisma.activity.update({
      where: { id },
      data: {
        ...data,
        date: adjustDate(new Date(data.date))!,
        plannedDate: adjustDate(data.plannedDate ? new Date(data.plannedDate) : null),
        plannedStartTime:
          data.plannedDate && data.plannedStartTime
            ? adjustDate(new Date(`${data.plannedDate}T${data.plannedStartTime}`))
            : null,
        plannedEndTime:
          data.plannedDate && data.plannedEndTime
            ? adjustDate(new Date(`${data.plannedDate}T${data.plannedEndTime}`))
            : null,
        startTime: data.startTime ? adjustDate(new Date(`${data.date}T${data.startTime}`)) : null,
        endTime: data.endTime ? adjustDate(new Date(`${data.date}T${data.endTime}`)) : null,
        publicTitle: data.publicTitle || null,
        category: data.category || null,
        mode: data.mode || null,
        clientId: data.clientId,
        userId: data.userId || null,
        contactId: data.contactId || null,
        documentId: data.documentId || null,
        serviceLineId: data.serviceLineId || null,
        invoiceId: data.invoiceId || null,
        projectId: data.projectId || null,
        phaseId: data.phaseId || null,
        hourlyRateSnapshot: hourlyRate,
        amountSnapshot: calculatedAmount,
      },
    });

    await sendWebhook({
      entity: 'activity',
      action: 'update',
      data: activity,
    });
  } catch (error) {
    console.error('Update activity error:', error);
    return {
      message: 'Errore database: aggiornamento attività non riuscito.',
    };
  }

  revalidatePath('/gestionale/dashboard');
  revalidatePath('/gestionale/dashboard/activities');
  revalidatePath('/gestionale/dashboard/activities/kanban');

  return {
    message: 'UPDATED',
    errors: {},
  };
}

export async function getActivitiesByPhaseId(phaseId: string) {
  return fetchActivitiesByPhaseId(phaseId);
}

export async function deleteActivity(id: string, _formData: FormData) {
  try {
    await prisma.activity.delete({
      where: { id },
    });
    revalidatePath('/gestionale/dashboard/activities');
  } catch (error) {
    console.error('Errore database: eliminazione attività non riuscita.', error);
    throw new Error('Errore database: eliminazione attività non riuscita.');
  }

  await sendWebhook({
    entity: 'activity',
    action: 'delete',
    data: { id },
  });
}

export async function importActivitiesToDocument(
  documentId: string,
  prevState: State,
  formData: FormData,
): Promise<State> {
  const activityIds = formData.getAll('activityIds') as string[];

  if (!activityIds || activityIds.length === 0) {
    return {
      message: 'Nessuna attività selezionata.',
    };
  }

  try {
    // Fetch activities to get details
    const activities = await prisma.activity.findMany({
        where: {
            id: {
                in: activityIds
            }
        },
        include: {
          client: true,
        },
    });

    for (const activity of activities) {
        // Resolve rate and amount from activity snapshot or client registry
        const rate = activity.hourlyRateSnapshot ?? activity.client?.hourlyRate ?? defaultHourlyRate;
        const amount = activity.amountSnapshot ?? (activity.hours * rate);

        // Create service line
        const serviceLine = await prisma.serviceLine.create({
            data: {
                documentId: documentId,
                type: 'SERVICE',
                description: `${activity.description} (${activity.hours}h)`,
                recurrence: 'NONE',
                startDate: activity.date,
                price: amount,
                status: 'ACTIVE',
            }
        });

        await sendWebhook({
          entity: 'serviceLine',
          action: 'create',
          data: serviceLine,
        });

        // Update activity
        const updatedActivity = await prisma.activity.update({
            where: { id: activity.id },
            data: {
                billingStatus: 'BILLED',
                serviceLineId: serviceLine.id,
                hourlyRateSnapshot: rate,
                amountSnapshot: amount
            }
        });

        await sendWebhook({
          entity: 'activity',
          action: 'update',
          data: updatedActivity,
        });
    }

  } catch (error) {
    return {
      message: 'Errore database: importazione attività non riuscita.',
    };
  }

  revalidatePath(`/gestionale/dashboard/documents/${documentId}/edit`);
  redirect(`/gestionale/dashboard/documents/${documentId}/edit`);
}

export async function createComment(
  prevState: CommentFormState,
  formData: FormData,
): Promise<CommentFormState> {
  const session = await auth();
  if (!session?.user?.id) {
    return { message: 'Utente non autenticato.' };
  }

  const validatedFields = CreateComment.safeParse({
    content: formData.get('content'),
    opportunityId: formData.get('opportunityId'),
  });

  if (!validatedFields.success) {
    return {
      errors: validatedFields.error.flatten().fieldErrors,
      message: 'Campi mancanti. Creazione commento non riuscita.',
    };
  }

  const data = validatedFields.data;

  try {
    const comment = await prisma.comment.create({
      data: {
        content: data.content,
        opportunityId: data.opportunityId,
        userId: session.user.id,
      },
    });

    await sendWebhook({
      entity: 'comment',
      action: 'create',
      data: comment,
    });
  } catch (error) {
    return {
      message: 'Errore database: creazione commento non riuscita.',
    };
  }

  revalidatePath(`/gestionale/dashboard/opportunities/${data.opportunityId}`);
  return { message: 'Commento aggiunto con successo.' };
}

export async function createInvoice(
  prevState: State,
  formData: FormData,
): Promise<State> {
  const validatedFields = CreateInvoice.safeParse({
    clientId: formData.get('clientId'),
    total: formData.get('total'),
    status: formData.get('status') || 'DRAFT',
    dueDate: formData.get('dueDate'),
  });

  if (!validatedFields.success) {
    return {
      errors: validatedFields.error.flatten().fieldErrors,
      message: 'Campi mancanti. Creazione fattura non riuscita.',
    };
  }

  const data = validatedFields.data;

  const useActivities = formData.get('useActivities') === 'on';
  const hourlyRateRaw = formData.get('hourlyRate');
  const hourlyRate = hourlyRateRaw ? parseFloat(hourlyRateRaw.toString()) : 0;
  const useDatiCassaPrevidenziale =
    formData.get('useDatiCassaPrevidenziale') === 'on';
  const invoiceDiscountRaw = formData.get('invoiceDiscount');
  const invoiceDiscount = invoiceDiscountRaw
    ? parseFloat(invoiceDiscountRaw.toString())
    : 0;

  let items: InvoiceItem[] = [];
  let total = data.total ?? 0;

  if (useActivities) {
    if (hourlyRate <= 0) {
      return {
        errors: {
          hourlyRate: ['Inserire una tariffa oraria per le attività.'],
        },
        message: 'Campi mancanti. Creazione fattura non riuscita.',
      };
    }

    try {
      const activities = await prisma.activity.findMany({
        where: {
          clientId: data.clientId,
          billingStatus: 'TO_BILL',
          billable: true,
        },
        orderBy: {
          date: 'asc',
        },
      });

      if (!activities.length) {
        return {
          message: 'Nessuna attività da fatturare per questo cliente.',
        };
      }

      for (const activity of activities) {
        const amount = activity.hours * hourlyRate;
        items.push({
          activityId: activity.id,
          description: activity.description,
          date: activity.date,
          hours: activity.hours,
          rate: hourlyRate,
          amount,
        });
      }

      total = items.reduce((sum, item) => sum + item.amount, 0);

      if (invoiceDiscount > 0 && invoiceDiscount < total) {
        total = total - invoiceDiscount;
      }

      const invoice = await prisma.invoice.create({
        data: {
          clientId: data.clientId,
          total,
          status: data.status,
          dueDate: data.dueDate ? new Date(data.dueDate) : null,
          items: {
            items,
            meta: {
              useDatiCassaPrevidenziale,
              invoiceDiscount: invoiceDiscount > 0 ? invoiceDiscount : undefined,
            },
          } as InvoiceItemsPayload,
        },
      });

      for (const item of items) {
        const updatedActivity = await prisma.activity.update({
          where: { id: item.activityId },
          data: {
            billingStatus: 'BILLED',
            invoiceId: invoice.id,
            hourlyRateSnapshot: item.rate,
            amountSnapshot: item.amount,
          },
        });

        await sendWebhook({
          entity: 'activity',
          action: 'update',
          data: updatedActivity,
        });
      }

      await sendWebhook({
        entity: 'invoice',
        action: 'create',
        data: invoice,
      });
    } catch (error) {
      return {
        message: 'Errore database: creazione fattura non riuscita.',
      };
    }
  } else {
    if (total <= 0) {
      return {
        errors: {
          total: ['Inserire il totale o utilizzare le attività da fatturare.'],
        },
        message: 'Campi mancanti. Creazione fattura non riuscita.',
      };
    }

    try {
      const invoice = await prisma.invoice.create({
        data: {
          clientId: data.clientId,
          total,
          status: data.status,
          dueDate: data.dueDate ? new Date(data.dueDate) : null,
          items: {
            items: [],
            meta: {
              useDatiCassaPrevidenziale,
              invoiceDiscount: invoiceDiscount > 0 ? invoiceDiscount : undefined,
            },
          } as InvoiceItemsPayload,
        },
      });

      await sendWebhook({
        entity: 'invoice',
        action: 'create',
        data: invoice,
      });
    } catch (error) {
      return {
        message: 'Errore database: creazione fattura non riuscita.',
      };
    }
  }

  revalidatePath('/gestionale/dashboard/invoices');
  redirect('/dashboard/invoices');
}

export async function updateInvoice(
  id: string,
  prevState: State,
  formData: FormData,
) {
  const invoice = await prisma.invoice.findUnique({
    where: { id },
  });

  const validatedFields = UpdateInvoice.safeParse({
    id,
    clientId: formData.get('clientId'),
    total: formData.get('total'),
    status: formData.get('status'),
    dueDate: formData.get('dueDate'),
  });

  if (!validatedFields.success) {
    return {
      errors: validatedFields.error.flatten().fieldErrors,
      message: 'Campi mancanti. Aggiornamento fattura non riuscito.',
    };
  }

  const data = validatedFields.data;

  const useDatiCassaPrevidenziale =
    formData.get('useDatiCassaPrevidenziale') === 'on';
  const invoiceDiscountRaw = formData.get('invoiceDiscount');
  const invoiceDiscount = invoiceDiscountRaw
    ? parseFloat(invoiceDiscountRaw.toString())
    : 0;

  let existingItemsPayload: InvoiceItemsPayload = [];

  if (invoice && invoice.items) {
    const raw = invoice.items as any;
    if (Array.isArray(raw)) {
      existingItemsPayload = raw as InvoiceItem[];
    } else if (raw && typeof raw === 'object') {
      existingItemsPayload = {
        items: (raw.items as InvoiceItem[]) || [],
        meta: raw.meta || {},
      };
    }
  }

  let items: InvoiceItem[] = [];
  let meta: InvoiceMeta | undefined;

  // Extract existing meta
  if (Array.isArray(existingItemsPayload)) {
    meta = {};
  } else {
    meta = existingItemsPayload.meta || {};
  }

  const itemsRaw = formData.get('items');
  if (itemsRaw && typeof itemsRaw === 'string') {
    try {
      items = JSON.parse(itemsRaw);
    } catch (e) {
      items = Array.isArray(existingItemsPayload)
        ? existingItemsPayload
        : existingItemsPayload.items || [];
    }
  } else {
    items = Array.isArray(existingItemsPayload)
      ? existingItemsPayload
      : existingItemsPayload.items || [];
  }

  const updatedMeta: InvoiceMeta = {
    ...meta,
    useDatiCassaPrevidenziale,
    invoiceDiscount: invoiceDiscount > 0 ? invoiceDiscount : undefined,
  };

  try {
    // Handle activity release if items are removed
    const newActivityIds = new Set(
      items
        .map((item) => item.activityId)
        .filter((id): id is string => !!id)
    );

    const currentLinkedActivities = await prisma.activity.findMany({
      where: { invoiceId: id },
    });

    for (const activity of currentLinkedActivities) {
      if (!newActivityIds.has(activity.id)) {
        const updatedActivity = await prisma.activity.update({
          where: { id: activity.id },
          data: {
            invoiceId: null,
            billingStatus: 'TO_BILL',
            hourlyRateSnapshot: null,
            amountSnapshot: null,
          },
        });

        await sendWebhook({
          entity: 'activity',
          action: 'update',
          data: updatedActivity,
        });
      }
    }

    const updatedInvoice = await prisma.invoice.update({
      where: { id },
      data: {
        total: data.total,
        status: data.status,
        dueDate: data.dueDate ? new Date(data.dueDate) : null,
        items: {
          items,
          meta: updatedMeta,
        } as InvoiceItemsPayload,
      },
    });

    await sendWebhook({
      entity: 'invoice',
      action: 'update',
      data: updatedInvoice,
    });
  } catch (error) {
    return {
      message: 'Errore database: aggiornamento fattura non riuscito.',
    };
  }

  revalidatePath('/gestionale/dashboard/invoices');
  redirect('/dashboard/invoices');
}

export async function deleteInvoice(id: string, _formData: FormData) {
  try {
    // 1. Find linked activities
    const linkedActivities = await prisma.activity.findMany({
      where: { invoiceId: id },
    });

    // 2. Release activities
    for (const activity of linkedActivities) {
      const updatedActivity = await prisma.activity.update({
        where: { id: activity.id },
        data: {
          invoiceId: null,
          billingStatus: 'TO_BILL',
          hourlyRateSnapshot: null,
          amountSnapshot: null,
        },
      });

      await sendWebhook({
        entity: 'activity',
        action: 'update',
        data: updatedActivity,
      });
    }

    // 3. Revert ServiceLine lastRenewalDate if applicable
    // Check if the invoice was generated from a recurring service line
    const invoice = await prisma.invoice.findUnique({
        where: { id },
        // Items is a Json field, so it's included by default in findUnique
    });

    // Strategy: If we linked ServiceLine to Invoice properly, we could just revert.
    // Since we don't have a direct relation field on Invoice to ServiceLine (only Activity->ServiceLine),
    // we rely on the logic that generated the invoice.
    // The generation logic sets ServiceLine.lastRenewalDate.
    // If we delete the invoice, we should ideally roll back that date.
    
    // However, without a direct foreign key or a specific log, it's risky to guess which ServiceLine it was.
    // BUT, the user said: "Se la elimino e poi la rigenero non funziona.. dobbiamo fare in modo che funzioni"
    // This implies they want the system to be ready to generate it AGAIN.
    // So we must reset the 'lastRenewalDate' of the corresponding ServiceLine.
    
    // Let's try to find the service line based on the invoice content or metadata if stored.
    // If we can't find it, we can't reset it.
    
    // Improvement: We should store the serviceLineId in the Invoice or InvoiceItem to make this robust.
    // For now, let's search for a ServiceLine that matches the client and the amount/recurrence if possible,
    // OR, better, let's look at the implementation of the generator again.
    
    // In the generator:
    // const description = `Canone ${recurrence.toLowerCase()} - ${line.description || line.article?.name || 'Servizio'} - ${periodName}`;
    // It creates an item with this description.
    
    if (invoice && invoice.items) {
        const items = invoice.items as any[];
        for (const item of items) {
             // Try to match description pattern to find the service line? Too brittle.
             // We need a robust way.
             
             // Let's check if we can add a field to Invoice to store the origin ServiceLineId?
             // Or maybe we can infer it from the context?
        }
    }
    
    // Wait, if I cannot identify the service line, I cannot reset it.
    // I need to modify the schema to link Invoice to ServiceLine OR 
    // I need to modify the generator to store the ServiceLineId in the invoice metadata (if exists) or items metadata.
    
    // Let's modify the generator to store metadata first (in the previous step, but I can't go back in time for existing ones).
    // But for the user's specific case, they just generated it.
    
    // Let's look at the Invoice model again.
    // model Invoice { ... items Json ... }
    
    // I will add logic here to attempt to find the service line if we can.
    // If I update the generator to store { serviceLineId: ... } in the item metadata, I can use it here.
    
    // PLAN:
    // 1. Modify the generator (API) to include serviceLineId in the item metadata.
    // 2. Modify this delete function to check for that metadata and revert the ServiceLine.
    
    // But first, let's implementing the delete logic assuming the metadata is there, 
    // and then I will update the generator.
    
    // Actually, I can check if there is a service line for this client with a lastRenewalDate close to the invoice creation date?
    // That's heuristic but might work for now.
    
    // BETTER PLAN:
    // 1. Update the cron API to store `serviceLineId` in the invoice item metadata.
    // 2. Update `deleteInvoice` to read this `serviceLineId` and revert `lastRenewalDate`.
    
    // Let's start by modifying the deleteInvoice to handle this, assuming I'll fix the generator next.
    
    if (invoice) {
        const items = invoice.items as any[];
        if (Array.isArray(items)) {
            for (const item of items) {
                if (item.meta && item.meta.serviceLineId) {
                    // Found it!
                    const serviceLineId = item.meta.serviceLineId;
                    const serviceLine = await prisma.serviceLine.findUnique({ where: { id: serviceLineId } });
                    
                    if (serviceLine && serviceLine.recurrence !== 'NONE') {
                        // Revert logic:
                        // We need to set lastRenewalDate back.
                        // But to what? 
                        // If Monthly, and we just generated it, lastRenewalDate is Today.
                        // We need to set it back to 1 month ago? Or just null if it was the first time?
                        
                        // If we set it to null, it might regenerate for all past periods if we are not careful.
                        // The generator logic:
                        // if (!lastRenewal) -> checks start date.
                        // if (lastRenewal) -> checks diff.
                        
                        // If we just generated "February" (e.g.), and we delete it.
                        // We want "February" to be generated again.
                        // So we need to set lastRenewalDate to "January" (or whatever it was before).
                        
                        // How do we know what it was before?
                        // We don't store "previousRenewalDate".
                        
                        // Heuristic:
                        // If Recurrence is MONTHLY, subtract 1 month from current lastRenewalDate.
                        // If QUARTERLY, subtract 3 months.
                        // If YEARLY, subtract 1 year.
                        
                        if (serviceLine.lastRenewalDate) {
                            const newDate = new Date(serviceLine.lastRenewalDate);
                            if (serviceLine.recurrence === 'MONTHLY') {
                                newDate.setMonth(newDate.getMonth() - 1);
                            } else if (serviceLine.recurrence === 'QUARTERLY') {
                                newDate.setMonth(newDate.getMonth() - 3);
                            } else if (serviceLine.recurrence === 'YEARLY') {
                                newDate.setFullYear(newDate.getFullYear() - 1);
                            }
                            
                            // Check if the new date is before start date?
                            // If so, maybe set to null? Or keep it as start date?
                            // If it goes before start date, logic "if (!lastRenewal)" might apply if we set to null.
                            // But "if (lastRenewal)" logic handles dates.
                            
                            await prisma.serviceLine.update({
                                where: { id: serviceLineId },
                                data: { lastRenewalDate: newDate }
                            });
                        }
                    }
                }
            }
        }
    }

    // 3. Delete invoice
    await prisma.invoice.delete({
      where: { id },
    });

    revalidatePath('/gestionale/dashboard/invoices');
  } catch (error) {
    console.error('Errore database: eliminazione fattura non riuscita.', error);
    throw new Error('Errore database: eliminazione fattura non riuscita.');
  }

  await sendWebhook({
    entity: 'invoice',
    action: 'delete',
    data: { id },
  });
}

export async function updateActivityDate(id: string, dateString: string) {
  try {
    const activity = await prisma.activity.update({
      where: { id },
      data: { date: new Date(dateString) },
    });
    revalidatePath('/gestionale/dashboard/activities');
    revalidatePath(`/gestionale/dashboard/activities/${id}`);
    
    await sendWebhook({
      entity: 'activity',
      action: 'update',
      data: activity,
    });
  } catch (error) {
    console.error('Database Error:', error);
    throw new Error('Failed to update activity date.');
  }
}

export async function updateActivityHours(id: string, hours: number) {
  try {
    const activity = await prisma.activity.update({
      where: { id },
      data: { hours },
    });
    revalidatePath('/gestionale/dashboard/activities');
    revalidatePath(`/gestionale/dashboard/activities/${id}`);

    await sendWebhook({
      entity: 'activity',
      action: 'update',
      data: activity,
    });
  } catch (error) {
    console.error('Database Error:', error);
    throw new Error('Failed to update activity hours.');
  }
}

export async function updateActivityTimes(
  id: string, 
  startTime: string | null, 
  endTime: string | null, 
  timezoneOffset: number = 0
) {
  try {
    const currentActivity = await prisma.activity.findUnique({
      where: { id },
      select: { date: true }
    });
    
    if (!currentActivity) throw new Error('Activity not found');
    
    // Use the date from the activity
    const dateObj = new Date(currentActivity.date);
    const dateStr = dateObj.toISOString().split('T')[0];
    
    const adjustDate = (date: Date | null) => {
        if (!date) return null;
        if (timezoneOffset === 0) return date;
        return new Date(date.getTime() + timezoneOffset * 60 * 1000);
    };

    let startDateTime: Date | null = null;
    let endDateTime: Date | null = null;

    if (startTime) {
        // Construct basic date time string
        const baseStart = new Date(`${dateStr}T${startTime}`);
        startDateTime = adjustDate(baseStart);
    }

    if (endTime) {
        const baseEnd = new Date(`${dateStr}T${endTime}`);
        endDateTime = adjustDate(baseEnd);
    }

    const activity = await prisma.activity.update({
      where: { id },
      data: { 
        startTime: startDateTime,
        endTime: endDateTime
      },
    });
    revalidatePath('/gestionale/dashboard/activities');
    revalidatePath(`/gestionale/dashboard/activities/${id}`);

    await sendWebhook({
      entity: 'activity',
      action: 'update',
      data: activity,
    });
  } catch (error) {
    console.error('Database Error:', error);
    throw new Error('Failed to update activity times.');
  }
}

export async function generateActivityTitle(payload: {
  type: string;
  mode: string;
  objective: string;
}) {
  const webhookUrl = process.env.N8N_TITLE_GENERATOR_WEBHOOK_URL;

  if (!webhookUrl) {
    console.error('N8N_TITLE_GENERATOR_WEBHOOK_URL not defined');
    return { error: 'Webhook URL not configured' };
  }

  try {
    const response = await fetch(webhookUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      throw new Error(`Webhook failed: ${response.statusText}`);
    }

    const data = await response.json();

    const title = data.title || data.output || data.text || data.result;
    
    if (typeof title === 'string') {
        return { title };
    }
    
    return { title: JSON.stringify(data) };

  } catch (error) {
    console.error('Title generation error:', error);
    return { error: 'Failed to generate title' };
  }
}

// ========== PROJECT TEMPLATE ACTIONS ==========

export async function createProjectTemplate(prevState: State, formData: FormData) {
  const validatedFields = CreateProjectTemplate.safeParse({
    name: formData.get('name'),
    description: formData.get('description'),
  });

  if (!validatedFields.success) {
    return {
      errors: validatedFields.error.flatten().fieldErrors,
      message: 'Campi mancanti. Creazione template non riuscita.',
    };
  }

  const data = validatedFields.data;

  try {
    await prisma.projectTemplate.create({
      data: {
        name: data.name,
        description: data.description || null,
      },
    });
  } catch (error) {
    return {
      message: 'Errore database: creazione template non riuscita.',
    };
  }

  revalidatePath('/gestionale/dashboard/project-templates');
  redirect('/dashboard/project-templates');
}

export async function updateProjectTemplate(
  id: string,
  prevState: State,
  formData: FormData,
) {
  const validatedFields = UpdateProjectTemplate.safeParse({
    id,
    name: formData.get('name'),
    description: formData.get('description'),
  });

  if (!validatedFields.success) {
    return {
      errors: validatedFields.error.flatten().fieldErrors,
      message: 'Campi mancanti. Aggiornamento template non riuscito.',
    };
  }

  const data = validatedFields.data;

  try {
    await prisma.projectTemplate.update({
      where: { id },
      data: {
        name: data.name,
        description: data.description || null,
      },
    });
  } catch (error) {
    return {
      message: 'Errore database: aggiornamento template non riuscito.',
    };
  }

  revalidatePath('/gestionale/dashboard/project-templates');
  redirect('/dashboard/project-templates');
}

export async function deleteProjectTemplate(id: string) {
  try {
    await prisma.projectTemplate.delete({
      where: { id },
    });
    revalidatePath('/gestionale/dashboard/project-templates');
  } catch (error) {
    throw new Error('Errore database: eliminazione template non riuscita.');
  }
}

// ========== TEMPLATE PHASE ACTIONS ==========

export async function createTemplatePhase(prevState: State, formData: FormData) {
  const validatedFields = CreateTemplatePhase.safeParse({
    templateId: formData.get('templateId'),
    name: formData.get('name'),
    description: formData.get('description'),
    plannedDuration: formData.get('plannedDuration'),
    order: formData.get('order') || '0',
  });

  if (!validatedFields.success) {
    return {
      errors: validatedFields.error.flatten().fieldErrors,
      message: 'Campi mancanti. Creazione fase template non riuscita.',
    };
  }

  const data = validatedFields.data;

  try {
    const phase = await prisma.templatePhase.create({
      data: {
        templateId: data.templateId,
        name: data.name,
        description: data.description || null,
        plannedDuration: data.plannedDuration,
        order: data.order,
      },
    });
    
    revalidatePath(`/gestionale/dashboard/project-templates/${data.templateId}`);
    return { message: 'Fase template creata con successo.', phaseId: phase.id };
  } catch (error) {
    return {
      message: 'Errore database: creazione fase template non riuscita.',
    };
  }
}

export async function updateTemplatePhase(
  id: string,
  prevState: State,
  formData: FormData,
) {
  const validatedFields = UpdateTemplatePhase.safeParse({
    id,
    templateId: formData.get('templateId'),
    name: formData.get('name'),
    description: formData.get('description'),
    plannedDuration: formData.get('plannedDuration'),
    order: formData.get('order') || '0',
  });

  if (!validatedFields.success) {
    return {
      errors: validatedFields.error.flatten().fieldErrors,
      message: 'Campi mancanti. Aggiornamento fase template non riuscito.',
    };
  }

  const data = validatedFields.data;

  try {
    await prisma.templatePhase.update({
      where: { id },
      data: {
        name: data.name,
        description: data.description || null,
        plannedDuration: data.plannedDuration,
        order: data.order,
      },
    });
  } catch (error) {
    return {
      message: 'Errore database: aggiornamento fase template non riuscito.',
    };
  }

  revalidatePath(`/gestionale/dashboard/project-templates/${data.templateId}`);
  return { message: 'Fase template aggiornata con successo.' };
}

export async function deleteTemplatePhase(id: string, templateId: string) {
  try {
    await prisma.templatePhase.delete({
      where: { id },
    });
    revalidatePath(`/dashboard/project-templates/${templateId}`);
    return { message: 'Fase template eliminata con successo.' };
  } catch (error) {
    throw new Error('Errore database: eliminazione fase template non riuscita.');
  }
}

// ========== TEMPLATE ACTIVITY ACTIONS ==========

export async function createTemplateActivity(prevState: State, formData: FormData) {
  const validatedFields = CreateTemplateActivity.safeParse({
    phaseId: formData.get('phaseId'),
    description: formData.get('description'),
    publicTitle: formData.get('publicTitle'),
    hours: formData.get('hours'),
    category: formData.get('category'),
    mode: formData.get('mode'),
    order: formData.get('order') || '0',
  });

  if (!validatedFields.success) {
    return {
      errors: validatedFields.error.flatten().fieldErrors,
      message: 'Campi mancanti. Creazione attività template non riuscita.',
    };
  }

  const data = validatedFields.data;

  try {
    await prisma.templateActivity.create({
      data: {
        phaseId: data.phaseId,
        description: data.description,
        publicTitle: data.publicTitle || null,
        hours: data.hours || 0,
        category: data.category || null,
        mode: data.mode || null,
        order: data.order,
      },
    });
  } catch (error) {
    return {
      message: 'Errore database: creazione attività template non riuscita.',
    };
  }

  const phase = await prisma.templatePhase.findUnique({
    where: { id: data.phaseId },
    select: { templateId: true },
  });

  if (phase) {
    revalidatePath(`/gestionale/dashboard/project-templates/${phase.templateId}`);
  }
  return { message: 'Attività template creata con successo.' };
}

export async function updateTemplateActivity(
  id: string,
  prevState: State,
  formData: FormData,
) {
  const validatedFields = UpdateTemplateActivity.safeParse({
    id,
    phaseId: formData.get('phaseId'),
    description: formData.get('description'),
    publicTitle: formData.get('publicTitle'),
    hours: formData.get('hours'),
    category: formData.get('category'),
    mode: formData.get('mode'),
    order: formData.get('order') || '0',
  });

  if (!validatedFields.success) {
    return {
      errors: validatedFields.error.flatten().fieldErrors,
      message: 'Campi mancanti. Aggiornamento attività template non riuscito.',
    };
  }

  const data = validatedFields.data;

  try {
    await prisma.templateActivity.update({
      where: { id },
      data: {
        description: data.description,
        publicTitle: data.publicTitle || null,
        hours: data.hours || 0,
        category: data.category || null,
        mode: data.mode || null,
        order: data.order,
      },
    });
  } catch (error) {
    return {
      message: 'Errore database: aggiornamento attività template non riuscito.',
    };
  }

  const phase = await prisma.templatePhase.findUnique({
    where: { id: data.phaseId },
    select: { templateId: true },
  });

  if (phase) {
    revalidatePath(`/gestionale/dashboard/project-templates/${phase.templateId}`);
  }
  return { message: 'Attività template aggiornata con successo.' };
}

export async function deleteTemplateActivity(id: string, templateId: string) {
  try {
    await prisma.templateActivity.delete({
      where: { id },
    });
    revalidatePath(`/dashboard/project-templates/${templateId}`);
    return { message: 'Attività template eliminata con successo.' };
  } catch (error) {
    throw new Error('Errore database: eliminazione attività template non riuscita.');
  }
}

export async function reorderTemplateActivity(
  activityId: string,
  direction: 'up' | 'down',
  templateId: string,
) {
  try {
    // Ottieni l'attività corrente
    const currentActivity = await prisma.templateActivity.findUnique({
      where: { id: activityId },
      select: { id: true, order: true, phaseId: true },
    });

    if (!currentActivity) {
      return { message: 'Attività non trovata.' };
    }

    // Ottieni tutte le attività della stessa fase, ordinate per order
    const allActivities = await prisma.templateActivity.findMany({
      where: { phaseId: currentActivity.phaseId },
      select: { id: true, order: true },
      orderBy: { order: 'asc' },
    });

    // Trova l'indice dell'attività corrente
    const currentIndex = allActivities.findIndex((a) => a.id === activityId);

    if (currentIndex === -1) {
      return { message: 'Attività non trovata nella fase.' };
    }

    // Determina l'indice di destinazione
    let targetIndex: number;
    if (direction === 'up') {
      targetIndex = currentIndex - 1;
      if (targetIndex < 0) {
        return { message: 'L\'attività è già la prima.' };
      }
    } else {
      targetIndex = currentIndex + 1;
      if (targetIndex >= allActivities.length) {
        return { message: 'L\'attività è già l\'ultima.' };
      }
    }

    // Scambia gli ordini
    const currentOrder = allActivities[currentIndex].order;
    const targetOrder = allActivities[targetIndex].order;

    // Aggiorna entrambe le attività
    await prisma.$transaction([
      prisma.templateActivity.update({
        where: { id: activityId },
        data: { order: targetOrder },
      }),
      prisma.templateActivity.update({
        where: { id: allActivities[targetIndex].id },
        data: { order: currentOrder },
      }),
    ]);

    revalidatePath(`/gestionale/dashboard/project-templates/${templateId}`);
    return { message: 'Ordine attività aggiornato con successo.' };
  } catch (error) {
    console.error('Errore riordinamento attività:', error);
    return { message: 'Errore durante il riordinamento dell\'attività.' };
  }
}

export async function reorderTemplateActivities(
  activityOrders: { id: string; order: number }[],
  templateId: string,
) {
  try {
    await prisma.$transaction(
      activityOrders.map(({ id, order }) =>
        prisma.templateActivity.update({
          where: { id },
          data: { order },
        })
      )
    );

    revalidatePath(`/gestionale/dashboard/project-templates/${templateId}`);
    return { message: 'Ordine attività aggiornato con successo.' };
  } catch (error) {
    console.error('Errore riordinamento attività:', error);
    return { message: 'Errore durante il riordinamento delle attività.' };
  }
}
